import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  Hotel, MapPin, Calendar, Plus, Trash2, Save, Edit,
  ChevronRight, ChevronLeft, Calculator, Truck, CheckCircle, X, Camera, Sparkles, Car, ToggleLeft, ToggleRight,
  CheckSquare, XSquare, Copy, ScrollText, RotateCcw
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { formatDateDMY } from "../../utils/dateFormat";
import DashboardLayout from "../../components/layouts/DashboardLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getDatesInRange = (startDate, endDate) => {
  if (!startDate || !endDate) return [];
  const dateArray = [];
  let currentDate = new Date(startDate);
  const stopDate = new Date(endDate);
  
  // Normalize time to avoid infinite loops if time differs
  currentDate.setHours(0,0,0,0);
  stopDate.setHours(0,0,0,0);

  let dayCount = 1;
  while (currentDate <= stopDate) {
    dateArray.push({
      day: dayCount,
      date: new Date(currentDate),
      dateStr: `${String(currentDate.getDate()).padStart(2,'0')}/${String(currentDate.getMonth()+1).padStart(2,'0')}/${currentDate.getFullYear()}`,
      weekday: currentDate.toLocaleDateString('en-GB', { weekday: 'long' })
    });
    currentDate.setDate(currentDate.getDate() + 1);
    dayCount++;
  }
  return dateArray;
};

const QuoteBuilder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [quote, setQuote] = useState(null);
  const [completingRevision, setCompletingRevision] = useState(false);
  const [activeTab, setActiveTab] = useState("hotel");

  // Multi-Option Accommodation
  const [accommodationOptions, setAccommodationOptions] = useState([{ label: "Option 1", hotels: [] }]);
  const [activeOption, setActiveOption] = useState(0);

  // Hotel State (for the active option)
  const hotels = accommodationOptions[activeOption]?.hotels || [];
  const setHotels = (newHotels) => {
    setAccommodationOptions(prev => {
      const updated = [...prev];
      updated[activeOption] = { ...updated[activeOption], hotels: typeof newHotels === 'function' ? newHotels(updated[activeOption].hotels) : newHotels };
      return updated;
    });
  };
  const [showHotelForm, setShowHotelForm] = useState(false);
  const [editingHotelIndex, setEditingHotelIndex] = useState(null);
  const [hotelForm, setHotelForm] = useState({
    city: "", hotel_name: "", hotel_category: "4 Star", check_in: "", check_out: "", nights: 0,
    room_type: "", meal_plan: "CP", rooms_count: 1, room_price: 0,
    extra_bed_toggle: false, extra_bed_count: 0, extra_bed_price: 0,
    cwb_toggle: false, cwb_count: 0, cwb_price: 0, cnb_toggle: false, cnb_count: 0, cnb_price: 0,
    total_cost: 0
  });

  // Step-by-step flow state
  const [currentStep, setCurrentStep] = useState(1); // 1: Accommodation, 2: Activities, 3: Summary
  const [savingStep, setSavingStep] = useState(false);

  // Resources State
  const [sightseeing, setSightseeing] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [hotelResources, setHotelResources] = useState([]); // Hotels from resources
  
  // Hotel Selection Mode
  const [hotelInputMode, setHotelInputMode] = useState("resource"); // "resource" or "manual"
  const [selectedResourceHotel, setSelectedResourceHotel] = useState(null);
  const [availableCities, setAvailableCities] = useState([]);
  const [availableHotelsForCity, setAvailableHotelsForCity] = useState([]);
  
  // Selected Activities
  const [selectedActivities, setSelectedActivities] = useState([]); 
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [currentDayForActivity, setCurrentDayForActivity] = useState(null);
  const [activitySearch, setActivitySearch] = useState("");

  // Enquiry edit modal (destination / dates / pax / client info)
  const [showEnquiryEdit, setShowEnquiryEdit] = useState(false);
  const [enquiryForm, setEnquiryForm] = useState({
    client_name: "", destination: "", covered_cities: "",
    pickup_location: "", drop_location: "",
    travel_date: "", return_date: "",
    pax_adults: 1,
    cwb: 0, cwb_age: "", cnb: 0, cnb_age: "",
    pax_infants: 0, infant_age: "",
    no_of_rooms: 1, hotel_category: "3-star", meal_plan: "CP",
    with_hotel: "yes", requirements: "",
    // kept in state but hidden from UI (used elsewhere)
    client_phone: "", client_email: "",
  });
  const [enquirySaving, setEnquirySaving] = useState(false);
  
  // Entry Tickets
  const [entryTickets, setEntryTickets] = useState([]);
  const [showEntryTicketModal, setShowEntryTicketModal] = useState(false);
  const [entryTicketSearch, setEntryTicketSearch] = useState("");
  
  // Manual Activity
  const [showManualActivityModal, setShowManualActivityModal] = useState(false);
  const [editingActivityIdx, setEditingActivityIdx] = useState(null); // null = add mode
  const [manualActivity, setManualActivity] = useState({ title: "", description: "", price: 0, tour_type: "SIC" });
  
  // Selected Transfers
  const [selectedTransfers, setSelectedTransfers] = useState([]);
  const [showTransferModal, setShowTransferModal] = useState(false);
  
  // Pricing
  const [markup, setMarkup] = useState(0);
  const [cabCost, setCabCost] = useState(0);
  const [markupMode, setMarkupMode] = useState("manual"); // "manual" or "percentage"
  const [markupPercentage, setMarkupPercentage] = useState(0);
  
  // Additional Cost Fields
  const [entryTicketCost, setEntryTicketCost] = useState({ adult: 0, cwb: 0, cnb: 0 });
  const [ferryCruiseCost, setFerryCruiseCost] = useState({ adult: 0, cwb: 0, cnb: 0 });
  const [miscCost, setMiscCost] = useState({ adult: 0, cwb: 0, cnb: 0 });
  const [markupPerPerson, setMarkupPerPerson] = useState({ adult: 0, cwb: 0, cnb: 0 });
  
  // Taxi Pricing Mode
  const [taxiPricingMode, setTaxiPricingMode] = useState("manual"); // "manual" or "auto"
  const [autoTaxiPricing, setAutoTaxiPricing] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [loadingTaxiPricing, setLoadingTaxiPricing] = useState(false);
  const [manualVehicleType, setManualVehicleType] = useState("");
  const [manualVehiclePrice, setManualVehiclePrice] = useState(0);
  // New transport fields (per user's spec)
  const [manualVehicleCount, setManualVehicleCount] = useState(1);
  const [manualVehicleAc, setManualVehicleAc] = useState("AC"); // AC / Non-AC
  const [manualVehicleCustomName, setManualVehicleCustomName] = useState(""); // shown when type === 'Others'

  // Inclusions & Exclusions
  const [inclusions, setInclusions] = useState("");
  const [exclusions, setExclusions] = useState("");
  const [templateLoaded, setTemplateLoaded] = useState(false);
  const [loadingTemplate, setLoadingTemplate] = useState(false);

  // Terms & Conditions
  const [termsConditions, setTermsConditions] = useState("");
  const [showTermsConditions, setShowTermsConditions] = useState(false);

  const fetchQuote = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/quotes/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuote(response.data);
      // If this quote was just duplicated, auto-open the Edit Enquiry modal
      // so admin can adjust destination / dates / pax before continuing.
      const isDupPending = response.data?.is_duplicate_pending_edit === true;
      setEnquiryForm({
        client_name: response.data.client_name || "",
        destination: (response.data.destination || "").replace(/\s*\(Copy\)\s*$/i, isDupPending ? "" : (response.data.destination?.endsWith(" (Copy)") ? "" : "")),
        covered_cities: response.data.covered_cities || "",
        pickup_location: response.data.pickup_location || "",
        drop_location: response.data.drop_location || "",
        travel_date: (response.data.travel_date || "").slice(0, 10),
        return_date: (response.data.return_date || "").slice(0, 10),
        pax_adults: response.data.pax_adults || 1,
        cwb: response.data.cwb || 0,
        cwb_age: response.data.cwb_age || "",
        cnb: response.data.cnb || 0,
        cnb_age: response.data.cnb_age || "",
        pax_infants: response.data.pax_infants || 0,
        infant_age: response.data.infant_age || "",
        no_of_rooms: response.data.no_of_rooms || 1,
        hotel_category: response.data.hotel_category || "3-star",
        meal_plan: response.data.meal_plan || "CP",
        with_hotel: response.data.with_hotel || "yes",
        requirements: response.data.requirements || "",
        client_phone: response.data.client_phone || "",
        client_email: response.data.client_email || "",
      });
      if (isDupPending) {
        // Prefill destination WITHOUT the (Copy) suffix so admin can rename cleanly
        setEnquiryForm(prev => ({ ...prev, destination: (response.data.destination || "").replace(/\s*\(Copy\)\s*$/i, "") }));
        setShowEnquiryEdit(true);
      }
      // Load accommodation options — force labels to "Option N" regardless of stored label
      if (response.data.accommodation_options && response.data.accommodation_options.length > 0) {
        const normalized = response.data.accommodation_options.map((opt, idx) => ({
          ...opt,
          label: `Option ${idx + 1}`
        }));
        setAccommodationOptions(normalized);
      } else if (response.data.hotels && response.data.hotels.length > 0) {
        // Backward compatibility: migrate single hotel list to option 1
        const optCount = response.data.hotel_options_count || 1;
        const options = [{ label: "Option 1", hotels: response.data.hotels }];
        for (let i = 1; i < optCount; i++) {
          options.push({ label: `Option ${i + 1}`, hotels: [] });
        }
        setAccommodationOptions(options);
      } else {
        const optCount = response.data.hotel_options_count || 1;
        const options = [];
        for (let i = 0; i < Math.max(optCount, 1); i++) {
          options.push({ label: `Option ${i + 1}`, hotels: [] });
        }
        setAccommodationOptions(options);
      }
      if (response.data.activities) setSelectedActivities(response.data.activities);
      if (response.data.transfers_details) setSelectedTransfers(response.data.transfers_details);
      if (response.data.entry_ticket_cost) setEntryTicketCost(response.data.entry_ticket_cost);
      if (response.data.ferry_cruise_cost) setFerryCruiseCost(response.data.ferry_cruise_cost);
      if (response.data.misc_cost) setMiscCost(response.data.misc_cost);
      if (response.data.manual_vehicle_type) setManualVehicleType(response.data.manual_vehicle_type);
      if (response.data.manual_vehicle_price) setManualVehiclePrice(response.data.manual_vehicle_price);
      if (response.data.manual_vehicle_count) setManualVehicleCount(response.data.manual_vehicle_count);
      if (response.data.manual_vehicle_ac) setManualVehicleAc(response.data.manual_vehicle_ac);
      if (response.data.manual_vehicle_custom_name) setManualVehicleCustomName(response.data.manual_vehicle_custom_name);
      
      // Load inclusions/exclusions - either from saved quote or fetch from template
      const quoteInclusions = response.data.inclusions;
      const quoteExclusions = response.data.exclusions;
      
      if (quoteInclusions) {
        setInclusions(quoteInclusions);
      }
      if (quoteExclusions) {
        setExclusions(quoteExclusions);
      }

      // Load terms & conditions
      if (response.data.terms_conditions) {
        setTermsConditions(response.data.terms_conditions);
        setShowTermsConditions(response.data.show_terms_conditions !== false);
      } else {
        setShowTermsConditions(false);
      }
      
      // If inclusions/exclusions are empty, try to fetch from destination template
      if (!quoteInclusions || !quoteExclusions) {
        const destination = response.data.destination;
        if (destination) {
          try {
            const templateRes = await axios.get(`${API}/package-templates/by-destination/${encodeURIComponent(destination)}`);
            if (templateRes.data) {
              let loaded = false;
              if (!quoteInclusions && templateRes.data.inclusions) {
                setInclusions(templateRes.data.inclusions);
                loaded = true;
              }
              if (!quoteExclusions && templateRes.data.exclusions) {
                setExclusions(templateRes.data.exclusions);
                loaded = true;
              }
              if (loaded) {
                setTemplateLoaded(true);
              }
            }
          } catch (templateError) {
            // Template not found for this destination - that's okay, fields stay empty
            console.log("No template found for destination:", destination);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching quote:", error);
      toast.error("Failed to load quote");
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  const fetchResources = useCallback(async () => {
    try {
      // Fetch all resources, filtering will be done in UI logic
      const [ssRes, trRes, destRes, hotelRes, etRes] = await Promise.all([
        axios.get(`${API}/sightseeing`),
        axios.get(`${API}/transfers`),
        axios.get(`${API}/destinations`),
        axios.get(`${API}/hotels`),
        axios.get(`${API}/entry-tickets`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setSightseeing(ssRes.data);
      setTransfers(trRes.data);
      setDestinations(destRes.data);
      setHotelResources(hotelRes.data);
      setEntryTickets(etRes.data || []);
      
      // Extract unique cities from hotel resources for the quote's destination
      if (hotelRes.data && hotelRes.data.length > 0) {
        const cities = [...new Set(hotelRes.data
          .filter(h => {
            // Filter hotels matching quote destination
            const hotelDest = (h.destination || '').toLowerCase();
            const quoteDest = (quote?.destination || '').toLowerCase();
            return hotelDest.includes(quoteDest) || quoteDest.includes(hotelDest);
          })
          .map(h => h.city)
          .filter(Boolean)
        )];
        setAvailableCities(cities);
      }
    } catch (error) {
      console.error("Error fetching resources:", error);
    }
  }, [quote]);

  useEffect(() => {
    if (id) fetchQuote();
  }, [id, fetchQuote]);

  useEffect(() => {
    if (quote) fetchResources();
  }, [quote, fetchResources]);

  // --- Hotel Logic ---
  const calculateHotelNights = (inDate, outDate) => {
    if (!inDate || !outDate) return 0;
    const start = new Date(inDate);
    const end = new Date(outDate);
    const diff = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 0;
  };

  const calculateHotelTotal = (form) => {
    const nights = calculateHotelNights(form.check_in, form.check_out);
    const roomCost = form.rooms_count * form.room_price * nights;
    const ebCost = form.extra_bed_toggle ? (form.extra_bed_count * form.extra_bed_price * nights) : 0;
    const cwbCost = form.cwb_toggle ? (form.cwb_count * form.cwb_price * nights) : 0;
    const cnbCost = form.cnb_toggle ? (form.cnb_count * form.cnb_price * nights) : 0;
    return roomCost + ebCost + cwbCost + cnbCost;
  };

  const handleHotelChange = (field, value) => {
    const updatedForm = { ...hotelForm, [field]: value };
    if (field === 'check_in' || field === 'check_out') {
      updatedForm.nights = calculateHotelNights(updatedForm.check_in, updatedForm.check_out);
    }
    updatedForm.total_cost = calculateHotelTotal(updatedForm);
    setHotelForm(updatedForm);
  };

  // Handle city selection - filter available hotels for that city
  const handleCitySelection = (city) => {
    if (city === "__manual__") {
      setHotelInputMode("manual");
      setHotelForm(prev => ({ ...prev, city: "" }));
      setAvailableHotelsForCity([]);
      setSelectedResourceHotel(null);
      return;
    }
    
    setHotelInputMode("resource");
    handleHotelChange('city', city);
    
    // Filter hotels for this city from resources
    const hotelsInCity = hotelResources.filter(h => 
      (h.city || '').toLowerCase() === city.toLowerCase()
    );
    setAvailableHotelsForCity(hotelsInCity);
    setSelectedResourceHotel(null);
  };

  // Handle hotel selection from resources - auto-populate pricing
  const handleHotelResourceSelection = (hotelId) => {
    if (hotelId === "__manual__") {
      setHotelInputMode("manual");
      setSelectedResourceHotel(null);
      setHotelForm(prev => ({ 
        ...prev, 
        hotel_name: "", 
        hotel_category: "4 Star",
        room_type: "",
        room_price: 0,
        extra_bed_price: 0,
        cwb_price: 0,
        cnb_price: 0
      }));
      return;
    }
    
    const hotel = hotelResources.find(h => h.id === hotelId);
    if (!hotel) return;
    
    setSelectedResourceHotel(hotel);
    
    // Get pricing from first rate period if available
    const firstPeriod = hotel.rate_periods?.[0];
    const firstRoom = firstPeriod?.room_pricing?.[0];
    const mealPlan = hotelForm.meal_plan?.toLowerCase() || 'cp';
    
    // Get room price based on selected meal plan
    const roomPrice = firstRoom ? (firstRoom[mealPlan] || firstRoom.cp || 0) : 0;
    
    // Get extra pricing
    const extraBedPrice = firstPeriod?.extra_bed?.[mealPlan] || firstPeriod?.extra_bed?.cp || 0;
    const cwbPrice = firstPeriod?.cwb?.[mealPlan] || firstPeriod?.cwb?.cp || 0;
    const cnbPrice = firstPeriod?.cnb?.[mealPlan] || firstPeriod?.cnb?.cp || 0;
    
    const updatedForm = {
      ...hotelForm,
      hotel_name: hotel.name,
      hotel_category: hotel.category || "4 Star",
      room_type: firstRoom?.room_category || "Deluxe Room",
      room_price: roomPrice,
      extra_bed_price: extraBedPrice,
      cwb_price: cwbPrice,
      cnb_price: cnbPrice
    };
    
    updatedForm.total_cost = calculateHotelTotal(updatedForm);
    setHotelForm(updatedForm);
  };

  // Update prices when meal plan changes (if hotel from resource)
  const handleMealPlanChange = (mealPlan) => {
    handleHotelChange('meal_plan', mealPlan);
    
    if (selectedResourceHotel) {
      const firstPeriod = selectedResourceHotel.rate_periods?.[0];
      const firstRoom = firstPeriod?.room_pricing?.[0];
      const mp = mealPlan.toLowerCase();
      
      const roomPrice = firstRoom ? (firstRoom[mp] || 0) : hotelForm.room_price;
      const extraBedPrice = firstPeriod?.extra_bed?.[mp] || hotelForm.extra_bed_price;
      const cwbPrice = firstPeriod?.cwb?.[mp] || hotelForm.cwb_price;
      const cnbPrice = firstPeriod?.cnb?.[mp] || hotelForm.cnb_price;
      
      const updatedForm = {
        ...hotelForm,
        meal_plan: mealPlan,
        room_price: roomPrice,
        extra_bed_price: extraBedPrice,
        cwb_price: cwbPrice,
        cnb_price: cnbPrice
      };
      updatedForm.total_cost = calculateHotelTotal(updatedForm);
      setHotelForm(updatedForm);
    }
  };

  // Check if extra bed / cwb / cnb should be available based on quote
  const hasExtraBedInQuote = () => {
    // Extra bed is needed if total pax > rooms * 2 (assuming 2 per room)
    return true; // Always allow admin to add extra bed if needed
  };
  
  const hasCwbInQuote = () => {
    const cwb = quote?.cwb || quote?.pax_cwb || quote?.cwb_count || 0;
    return cwb > 0;
  };
  
  const hasCnbInQuote = () => {
    const cnb = quote?.cnb || quote?.pax_cnb || quote?.cnb_count || 0;
    return cnb > 0;
  };

  const addHotel = () => {
    if (!hotelForm.city || !hotelForm.hotel_name || !hotelForm.check_in) {
      toast.error("Please fill required hotel fields");
      return;
    }
    
    if (editingHotelIndex !== null) {
      // Update existing hotel
      const updatedHotels = [...hotels];
      updatedHotels[editingHotelIndex] = hotelForm;
      setHotels(updatedHotels);
      setEditingHotelIndex(null);
      toast.success("Accommodation updated!");
    } else {
      // Add new hotel
      setHotels([...hotels, hotelForm]);
      toast.success("Accommodation added!");
    }
    
    setShowHotelForm(false);
    resetHotelFormState();
  };
  
  const resetHotelFormState = () => {
    setHotelForm({
      city: "", hotel_name: "", hotel_category: "4 Star", check_in: "", check_out: "", nights: 0,
      room_type: "", meal_plan: "CP", rooms_count: 1, room_price: 0,
      extra_bed_toggle: false, extra_bed_count: 0, extra_bed_price: 0,
      cwb_toggle: false, cwb_count: 0, cwb_price: 0, cnb_toggle: false, cnb_count: 0, cnb_price: 0,
      total_cost: 0
    });
    setHotelInputMode("resource");
    setSelectedResourceHotel(null);
    setAvailableHotelsForCity([]);
  };

  // Edit existing hotel
  const editHotel = (index) => {
    const hotel = hotels[index];
    setHotelForm({
      city: hotel.city || "",
      hotel_name: hotel.hotel_name || "",
      hotel_category: hotel.hotel_category || "4 Star",
      check_in: hotel.check_in || "",
      check_out: hotel.check_out || "",
      nights: hotel.nights || 0,
      room_type: hotel.room_type || "",
      meal_plan: hotel.meal_plan || "CP",
      rooms_count: hotel.rooms_count || 1,
      room_price: hotel.room_price || 0,
      extra_bed_toggle: hotel.extra_bed_toggle || false,
      extra_bed_count: hotel.extra_bed_count || 0,
      extra_bed_price: hotel.extra_bed_price || 0,
      cwb_toggle: hotel.cwb_toggle || false,
      cwb_count: hotel.cwb_count || 0,
      cwb_price: hotel.cwb_price || 0,
      cnb_toggle: hotel.cnb_toggle || false,
      cnb_count: hotel.cnb_count || 0,
      cnb_price: hotel.cnb_price || 0,
      total_cost: hotel.total_cost || 0
    });
    setEditingHotelIndex(index);
    setShowHotelForm(true);
  };

  const removeHotel = (index) => {
    const newHotels = [...hotels];
    newHotels.splice(index, 1);
    setHotels(newHotels);
  };

  // Smart date handling for accommodation
  const openAddHotelForm = () => {
    let smartCheckIn = "";
    let smartCheckOut = "";
    
    // Format date to YYYY-MM-DD for input
    const formatDateForInput = (dateStr) => {
      if (!dateStr) return "";
      const date = new Date(dateStr);
      return date.toISOString().split('T')[0];
    };
    
    if (hotels.length === 0) {
      // First hotel: Use enquiry travel date
      smartCheckIn = formatDateForInput(quote?.travel_date);
    } else {
      // Subsequent hotels: Use last hotel's check-out date
      const lastHotel = hotels[hotels.length - 1];
      smartCheckIn = lastHotel.check_out || "";
    }
    
    // Calculate nights and set initial form
    const nights = smartCheckIn && smartCheckOut ? calculateHotelNights(smartCheckIn, smartCheckOut) : 0;
    
    // Auto-set Extra Bed for odd adult counts (3, 5, 7)
    const adults = quote?.pax_adults || 0;
    const needsExtraBed = adults > 0 && adults % 2 !== 0;
    
    // Auto-set CWB/CNB from quote request
    const quoteCwb = quote?.cwb || quote?.cwb_count || 0;
    const quoteCnb = quote?.cnb || quote?.cnb_count || 0;
    
    setHotelForm({
      city: "", hotel_name: "", hotel_category: "4 Star",
      check_in: smartCheckIn, 
      check_out: smartCheckOut, 
      nights: nights,
      room_type: "", meal_plan: "CP", rooms_count: 1, room_price: 0,
      extra_bed_toggle: needsExtraBed, extra_bed_count: needsExtraBed ? 1 : 0, extra_bed_price: 0,
      cwb_toggle: quoteCwb > 0, cwb_count: quoteCwb, cwb_price: 0,
      cnb_toggle: quoteCnb > 0, cnb_count: quoteCnb, cnb_price: 0,
      total_cost: 0
    });
    // Reset selection state
    setHotelInputMode("resource");
    setSelectedResourceHotel(null);
    setAvailableHotelsForCity([]);
    setShowHotelForm(true);
  };

  // Get max allowed check-out date (enquiry return date)
  const getMaxCheckOutDate = () => {
    if (!quote?.return_date) return "";
    return new Date(quote.return_date).toISOString().split('T')[0];
  };

  // Get min check-in date (previous hotel check-out or enquiry start)
  const getMinCheckInDate = () => {
    if (hotels.length === 0 && quote?.travel_date) {
      return new Date(quote.travel_date).toISOString().split('T')[0];
    }
    return "";
  };

  // --- Activity Logic ---
  const openActivityModal = (day) => {
    setCurrentDayForActivity(day);
    setShowActivityModal(true);
  };

  // Canonicalize any tour/transfer-type spelling (catalogue resources use "Private"/"SIC",
  // manual activities use "PVT"/"SIC"/"NONE") down to one consistent set: SIC | PVT | NONE.
  const normalizeTourType = (value) => {
    const v = (value || "").toString().trim().toLowerCase();
    if (v === "sic" || v === "seat_in_coach" || v === "seat-in-coach") return "SIC";
    if (v === "pvt" || v === "private") return "PVT";
    if (v === "none" || v === "no_transfer" || v === "no-transfer" || v === "no transfer") return "NONE";
    return "SIC";
  };

  const addActivity = (tour) => {
    setSelectedActivities([...selectedActivities, {
      day: currentDayForActivity,
      activity_id: tour.id,
      title: tour.tour_name,
      description: tour.description || "",
      type: tour.type || "sightseeing",
      tour_type: normalizeTourType(tour.type),
      price: tour.price_adult,
      price_child: tour.price_child || 0,
      billable: true, // Default ON
      custom_image: tour.image || ""
    }]);
    toast.success("Activity added!");
    setShowActivityModal(false);
  };

  const addEntryTicket = (ticket) => {
    setSelectedActivities([...selectedActivities, {
      day: currentDayForActivity,
      activity_id: ticket.id,
      title: ticket.attraction_name || ticket.name || ticket.tour_name,
      description: ticket.description || "",
      type: "entry_ticket",
      price: ticket.price_adult || ticket.price || 0,
      price_child: ticket.price_child || 0,
      billable: true,
      custom_image: ticket.image || ""
    }]);
    toast.success("Entry ticket added!");
    setShowEntryTicketModal(false);
  };

  const addManualActivity = () => {
    if (!manualActivity.title.trim()) {
      toast.error("Activity name is required");
      return;
    }
    if (editingActivityIdx !== null) {
      // Edit mode — patch the existing activity in place.
      const newActs = [...selectedActivities];
      newActs[editingActivityIdx] = {
        ...newActs[editingActivityIdx],
        title: manualActivity.title,
        description: manualActivity.description || "",
        price: parseFloat(manualActivity.price) || 0,
        tour_type: manualActivity.tour_type || "SIC",
      };
      setSelectedActivities(newActs);
      toast.success("Activity updated!");
    } else {
      setSelectedActivities([...selectedActivities, {
        day: currentDayForActivity,
        activity_id: `manual-${Date.now()}`,
        title: manualActivity.title,
        description: manualActivity.description || "",
        type: "sightseeing",
        price: parseFloat(manualActivity.price) || 0,
        tour_type: manualActivity.tour_type || "SIC",
        billable: true,
        custom_image: ""
      }]);
      toast.success("Manual activity added!");
    }
    setManualActivity({ title: "", description: "", price: 0, tour_type: "SIC" });
    setEditingActivityIdx(null);
    setShowManualActivityModal(false);
  };

  // Open the manual-activity modal in edit mode pre-filled with the activity's values.
  const openEditActivity = (idx) => {
    const a = selectedActivities[idx];
    if (!a) return;
    setEditingActivityIdx(idx);
    setCurrentDayForActivity(a.day);
    const inferredTourType = normalizeTourType(a.tour_type || a.type);
    setManualActivity({
      title: a.title || "",
      description: a.description || "",
      price: a.price || 0,
      tour_type: inferredTourType,
    });
    setShowManualActivityModal(true);
  };


  const removeActivity = (index) => {
    const newActs = [...selectedActivities];
    newActs.splice(index, 1);
    setSelectedActivities(newActs);
  };

  const toggleActivityBillable = (index) => {
    const newActs = [...selectedActivities];
    newActs[index].billable = !newActs[index].billable;
    setSelectedActivities(newActs);
  };

  const generateActivityImage = (index) => {
    const activity = selectedActivities[index];
    const query = encodeURIComponent(`${activity.title} ${quote.destination}`);
    const url = `https://source.unsplash.com/featured/?${query}`;
    
    const newActs = [...selectedActivities];
    newActs[index].custom_image = url;
    setSelectedActivities(newActs);
    toast.success("AI Image generated!");
  };

  const handleImageUpload = (e, index) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const newActs = [...selectedActivities];
        newActs[index].custom_image = reader.result;
        setSelectedActivities(newActs);
        toast.success("Image uploaded!");
      };
      reader.readAsDataURL(file);
    }
  };

  // --- Transfer Logic ---
  const addTransfer = (transferItem) => {
      // Default days to duration if per_day
      const days = transferItem.rate_type === 'per_day' ? (quote?.nights || 5) : 1;
      const initialCost = transferItem.price * days;
      
      setSelectedTransfers([...selectedTransfers, {
          type: transferItem.vehicle_type,
          rate_type: transferItem.rate_type,
          base_price: transferItem.price,
          days: days,
          total_cost: initialCost,
          details: transferItem.destination
      }]);
      setShowTransferModal(false);
      toast.success("Transport added!");
  };

  const updateTransferDays = (index, days) => {
      const newTransfers = [...selectedTransfers];
      const item = newTransfers[index];
      if (item.rate_type === 'per_day') {
          item.days = parseInt(days) || 1;
          item.total_cost = item.base_price * item.days;
          setSelectedTransfers(newTransfers);
      }
  };

  const updateTransferCost = (index, cost) => {
      const newTransfers = [...selectedTransfers];
      newTransfers[index].total_cost = parseFloat(cost) || 0;
      setSelectedTransfers(newTransfers);
  };

  const removeTransfer = (index) => {
      const newTransfers = [...selectedTransfers];
      newTransfers.splice(index, 1);
      setSelectedTransfers(newTransfers);
  };

  // --- Pricing Logic ---
  const getTotalHotelCost = () => hotels.reduce((sum, h) => sum + h.total_cost, 0);
  const getTotalActivityCost = () => selectedActivities.reduce((sum, a) => a.billable ? sum + (a.price || 0) : sum, 0);
  // Actual per-child price entered on the catalogue item (e.g. Sightseeing's "Child Price"
  // field) — a child's ticket/tour cost doesn't depend on whether they need a hotel bed, so
  // this one figure applies to both CWB and CNB.
  const getTotalActivityChildCost = () => selectedActivities.reduce((sum, a) => a.billable ? sum + (a.price_child || 0) : sum, 0);
  const getTotalTransportCost = () => selectedTransfers.reduce((sum, t) => sum + t.total_cost, 0);
  
  // Get number of travelers from enquiry (not from hotel config sum)
  const getAdultCount = () => quote?.pax_adults || 0;
  const getCwbCount = () => quote?.cwb || quote?.pax_cwb || quote?.cwb_count || 0;
  const getCnbCount = () => quote?.cnb || quote?.pax_cnb || quote?.cnb_count || 0;
  
  // Per Person Breakdown Calculations
  // Extra Bed is a flat surcharge (not tied to the adult/CWB/CNB pax split), so it's pulled
  // out of the room cost and surfaced as its own breakdown line — otherwise toggling it just
  // nudges the "Accommodation" per-adult figure for everyone with no visible cause.
  const getExtraBedTotal = () => hotels.reduce((sum, h) => sum + (h.extra_bed_toggle ? (h.extra_bed_count || 0) * (h.extra_bed_price || 0) * (h.nights || 0) : 0), 0);

  const getAccommodationPerPerson = () => {
    const total = getTotalHotelCost();
    const adults = getAdultCount() || 1;
    // For accommodation, CWB and CNB costs are already included in hotel total
    // We divide room cost among adults, and add CWB/CNB specific costs
    // (Extra Bed cost is intentionally left inside roomCost — see getExtraBedTotal() above,
    // which surfaces it as its own read-only breakdown line without changing this total,
    // so it keeps getting the same markup treatment as the rest of the room cost.)
    const cwbTotal = hotels.reduce((sum, h) => sum + (h.cwb_toggle ? h.cwb_count * h.cwb_price * h.nights : 0), 0);
    const cnbTotal = hotels.reduce((sum, h) => sum + (h.cnb_toggle ? h.cnb_count * h.cnb_price * h.nights : 0), 0);
    const roomCost = total - cwbTotal - cnbTotal;
    return {
      adult: adults > 0 ? Math.round(roomCost / adults) : 0,
      cwb: getCwbCount() > 0 ? Math.round(cwbTotal / getCwbCount()) : 0,
      cnb: getCnbCount() > 0 ? Math.round(cnbTotal / getCnbCount()) : 0
    };
  };

  const getActivitiesPerPerson = () => {
    // Every activity's price is entered per-adult (see the "Price (per adult)" field), so
    // getTotalActivityCost() is already the correct per-adult figure — it must NOT be divided
    // by adult count again (that previously undercharged by a factor of the adult headcount).
    const total = getTotalActivityCost();
    // CWB/CNB use the catalogue's actual "Child Price" (getTotalActivityChildCost) rather than
    // an arbitrary 50%/25% share of the adult rate — that guess ignored whatever price was
    // really entered for children (e.g. a tour priced the same for adults and children).
    const childTotal = getTotalActivityChildCost();
    return {
      adult: Math.round(total),
      cwb: getCwbCount() > 0 ? Math.round(childTotal) : 0,
      cnb: getCnbCount() > 0 ? Math.round(childTotal) : 0
    };
  };

  const getTaxiPerPerson = () => {
    const total = taxiPricingMode === "manual" ? parseFloat(manualVehiclePrice || 0) : parseFloat(cabCost || 0);
    const adultCount = getAdultCount() || 1;
    const perAdult = adultCount > 0 ? Math.round(total / adultCount) : 0;
    // CWB and CNB get 0 taxi cost by default (children don't pay for taxi separately)
    return { adult: perAdult, cwb: 0, cnb: 0 };
  };

  // Calculate markup based on mode
  const getMarkupAmount = (baseCost) => {
    if (markupMode === "percentage") {
      return Math.round(baseCost * (markupPercentage / 100));
    }
    return { 
      adult: markupPerPerson.adult || 0, 
      cwb: markupPerPerson.cwb || 0, 
      cnb: markupPerPerson.cnb || 0 
    };
  };

  // Calculate total cost per person
  const getTotalPerPerson = () => {
    const accom = getAccommodationPerPerson();
    const activities = getActivitiesPerPerson();
    const taxi = getTaxiPerPerson();
    const entry = entryTicketCost;
    const ferry = ferryCruiseCost;
    const misc = miscCost;
    
    const adultSubtotal = accom.adult + activities.adult + entry.adult + ferry.adult + taxi.adult + misc.adult;
    const cwbSubtotal = accom.cwb + activities.cwb + entry.cwb + ferry.cwb + taxi.cwb + misc.cwb;
    const cnbSubtotal = accom.cnb + activities.cnb + entry.cnb + ferry.cnb + taxi.cnb + misc.cnb;
    
    let markupAdult, markupCwb, markupCnb;
    if (markupMode === "percentage") {
      markupAdult = Math.round(adultSubtotal * (markupPercentage / 100));
      markupCwb = Math.round(cwbSubtotal * (markupPercentage / 100));
      markupCnb = Math.round(cnbSubtotal * (markupPercentage / 100));
    } else {
      markupAdult = markupPerPerson.adult;
      markupCwb = markupPerPerson.cwb;
      markupCnb = markupPerPerson.cnb;
    }
    
    return {
      adult: adultSubtotal + markupAdult,
      cwb: cwbSubtotal + markupCwb,
      cnb: cnbSubtotal + markupCnb,
      markupAdult,
      markupCwb,
      markupCnb
    };
  };

  const getGrandTotal = () => {
    const pp = getTotalPerPerson();
    return (pp.adult * getAdultCount()) + (pp.cwb * getCwbCount()) + (pp.cnb * getCnbCount());
  };

  // Calculate per-person pricing for a specific option (used in save)
  const getOptionPricing = (optionIdx) => {
    const optHotels = accommodationOptions[optionIdx]?.hotels || [];
    const adults = getAdultCount() || 1;
    
    const totalHotel = optHotels.reduce((sum, h) => sum + (h.total_cost || 0), 0);
    const cwbTotal = optHotels.reduce((sum, h) => sum + (h.cwb_toggle ? h.cwb_count * h.cwb_price * h.nights : 0), 0);
    const cnbTotal = optHotels.reduce((sum, h) => sum + (h.cnb_toggle ? h.cnb_count * h.cnb_price * h.nights : 0), 0);
    const roomCost = totalHotel - cwbTotal - cnbTotal;
    const cwbCount = getCwbCount();
    const cnbCount = getCnbCount();
    
    const accom = {
      adult: adults > 0 ? Math.round(roomCost / adults) : 0,
      cwb: cwbCount > 0 ? Math.round(cwbTotal / cwbCount) : 0,
      cnb: cnbCount > 0 ? Math.round(cnbTotal / cnbCount) : 0
    };
    
    const activities = getActivitiesPerPerson();
    const taxi = getTaxiPerPerson();
    const entry = entryTicketCost;
    const ferry = ferryCruiseCost;
    const misc = miscCost;
    
    const adultSub = accom.adult + activities.adult + entry.adult + ferry.adult + taxi.adult + misc.adult;
    const cwbSub = accom.cwb + activities.cwb + entry.cwb + ferry.cwb + taxi.cwb + misc.cwb;
    const cnbSub = accom.cnb + activities.cnb + entry.cnb + ferry.cnb + taxi.cnb + misc.cnb;
    
    let mA, mC, mN;
    if (markupMode === "percentage") {
      mA = Math.round(adultSub * (markupPercentage / 100));
      mC = Math.round(cwbSub * (markupPercentage / 100));
      mN = Math.round(cnbSub * (markupPercentage / 100));
    } else {
      mA = markupPerPerson.adult;
      mC = markupPerPerson.cwb;
      mN = markupPerPerson.cnb;
    }
    
    const adult = adultSub + mA;
    const cwb = cwbSub + mC;
    const cnb = cnbSub + mN;
    
    return {
      adult,
      cwb,
      cnb,
      grandTotal: (adult * (getAdultCount())) + (cwb * cwbCount) + (cnb * cnbCount)
    };
  };

  // --- Taxi Pricing Logic ---
  const calculateNights = () => {
    if (quote?.nights) return quote.nights;
    if (quote?.travel_date && quote?.return_date) {
      const start = new Date(quote.travel_date);
      const end = new Date(quote.return_date);
      return Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    }
    return null;
  };

  const fetchAutoTaxiPricing = async () => {
    if (!quote?.destination) {
      toast.error("Quote destination not found");
      return;
    }
    
    const nights = calculateNights();
    
    setLoadingTaxiPricing(true);
    try {
      const params = { destination: quote.destination };
      if (nights) params.nights = nights;
      
      const response = await axios.get(`${API}/taxi-pricing/search`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Handle both single object and array response
      const data = response.data;
      if (data && (data.vehicles || (Array.isArray(data) && data.length > 0))) {
        const pricing = Array.isArray(data) ? data[0] : data;
        setAutoTaxiPricing(pricing);
        toast.success(`Found taxi pricing for ${quote.destination}${nights ? ` - ${nights}N` : ''}`);
      } else {
        setAutoTaxiPricing(null);
        toast.info(`No automatic pricing found for ${quote.destination}${nights ? ` - ${nights}N` : ''}`);
      }
    } catch (error) {
      console.error("Error fetching taxi pricing:", error);
      setAutoTaxiPricing(null);
    } finally {
      setLoadingTaxiPricing(false);
    }
  };

  const handleTaxiModeChange = (mode) => {
    setTaxiPricingMode(mode);
    if (mode === "auto") {
      fetchAutoTaxiPricing();
    } else {
      setSelectedVehicle(null);
    }
  };

  const selectAutoVehicle = (vehicle) => {
    setSelectedVehicle(vehicle);
    setCabCost(vehicle.price);
    toast.success(`${vehicle.vehicle_type} selected - ₹${vehicle.price.toLocaleString()}`);
  };

  // Load Inclusions & Exclusions from destination template
  const loadTemplateFromResources = async () => {
    if (!quote?.destination) {
      toast.error("No destination found in quote");
      return;
    }
    
    setLoadingTemplate(true);
    try {
      const templateRes = await axios.get(`${API}/package-templates/by-destination/${encodeURIComponent(quote.destination)}`);
      if (templateRes.data) {
        if (templateRes.data.inclusions) {
          setInclusions(templateRes.data.inclusions);
        }
        if (templateRes.data.exclusions) {
          setExclusions(templateRes.data.exclusions);
        }
        setTemplateLoaded(true);
        toast.success(`Template loaded for ${quote.destination}`);
      } else {
        toast.info(`No template found for ${quote.destination}. Please add one in Resources > Inc & Exc.`);
      }
    } catch (error) {
      toast.info(`No template found for ${quote.destination}. Please add one in Resources > Inc & Exc.`);
    } finally {
      setLoadingTemplate(false);
    }
  };

  // Step 1: Save Accommodation and move to Activities
  const saveAccommodationAndNext = async () => {
    const hasAnyHotels = accommodationOptions.some(opt => opt.hotels?.length > 0);
    if (!hasAnyHotels) {
      toast.error("Please add at least one accommodation in any option");
      return;
    }
    
    setSavingStep(true);
    try {
      // Don't send `status` if the quote is already in a terminal state — agent may
      // have confirmed booking / marked lost. The backend also defends against this.
      const TERMINAL = ['booked', 'confirmed', 'lost', 'cancelled'];
      const payload = {
        hotels: accommodationOptions[0]?.hotels || [],
        accommodation_options: accommodationOptions,
      };
      if (!TERMINAL.includes(quote?.status)) {
        payload.status = quote?.status === 'new' ? 'in_progress' : quote?.status;
      }
      await axios.put(`${API}/quotes/${id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("All accommodation options saved!");
      setCurrentStep(2);
      setActiveTab("activity");
    } catch (error) {
      console.error("Error saving accommodation:", error);
      toast.error("Failed to save accommodation");
    } finally {
      setSavingStep(false);
    }
  };

  // Step 2: Save Activities and move to Summary
  const saveActivitiesAndNext = async () => {
    setSavingStep(true);
    try {
      await axios.put(`${API}/quotes/${id}`, {
        hotels,
        activities: selectedActivities,
        transfers_details: selectedTransfers
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Activities saved!");
      setCurrentStep(3);
      setActiveTab("transport");
    } catch (error) {
      console.error("Error saving activities:", error);
      toast.error("Failed to save activities");
    } finally {
      setSavingStep(false);
    }
  };

  // Go back to previous step
  const goToPreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      if (currentStep === 2) setActiveTab("hotel");
      if (currentStep === 3) setActiveTab("activity");
    }
  };


  const handleCompleteRevision = async () => {
    try {
      setCompletingRevision(true);
      await axios.post(`${API}/quotes/${id}/complete-revision`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Revision completed! Quote updated.");
      setQuote(prev => ({ ...prev, status: "quoted" }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to complete revision");
    } finally { setCompletingRevision(false); }
  };


  const saveQuote = async () => {
    try {
      const pp = getTotalPerPerson();
      
      // Build day-wise itinerary helper
      const tripDates = getDatesInRange(quote?.travel_date, quote?.return_date);
      const buildItinerary = (optionHotels) => {
        return tripDates.map((dateInfo) => {
          const hotelForDay = optionHotels.find(h => {
            if (!h.check_in || !h.check_out) return false;
            const checkIn = new Date(h.check_in);
            const checkOut = new Date(h.check_out);
            const currentDate = new Date(dateInfo.date);
            checkIn.setHours(0,0,0,0);
            checkOut.setHours(0,0,0,0);
            currentDate.setHours(0,0,0,0);
            return currentDate >= checkIn && currentDate < checkOut;
          });
          
          const activitiesForDay = selectedActivities
            .filter(a => a.day === dateInfo.day)
            .map(a => ({
              name: a.title || a.tour_name || a.name || '',
              description: a.description || '',
              type: a.type || 'sightseeing',
              tour_type: normalizeTourType(a.tour_type || a.type),
              price: a.price || 0
            }));
          const sightseeingNamesForDay = activitiesForDay
            .filter(a => a.type !== 'entry_ticket')
            .map(a => a.name)
            .filter(Boolean);

          const transferForDay = selectedTransfers.find(t => t.day === dateInfo.day);

          // Derive transfer type (SIC / Private / No Transfer) from the day's activities if no
          // explicit transfer override was set on the Transport tab.
          const activityTourType = activitiesForDay.find(a => a.type !== 'entry_ticket')?.tour_type || null;
          const activityTransferType = activityTourType === 'PVT' ? 'private'
            : activityTourType === 'NONE' ? 'none'
            : activityTourType === 'SIC' ? 'sic'
            : null;

          return {
            day: dateInfo.day,
            date: dateInfo.dateStr,
            title: hotelForDay?.city || quote?.destination || 'Day ' + dateInfo.day,
            city: hotelForDay?.city || '',
            hotel: hotelForDay ? `${hotelForDay.hotel_name} (${hotelForDay.hotel_category})` : null,
            hotel_details: hotelForDay || null,
            activities: activitiesForDay,
            sightseeing: sightseeingNamesForDay.join(', '),
            transfer_type: transferForDay?.transfer_type || activityTransferType || null,
            vehicle: transferForDay?.type || selectedVehicle?.vehicle_type || (manualVehicleType === "Others" ? (manualVehicleCustomName || "Others") : manualVehicleType) || null,
            description: sightseeingNamesForDay.length > 0 
              ? `Sightseeing: ${sightseeingNamesForDay.join(', ')}` 
              : (hotelForDay ? `Check-in at ${hotelForDay.hotel_name}` : '')
          };
        });
      };

      // Build accommodation_options with per-option pricing and itinerary
      const optionsPayload = accommodationOptions.map((opt, idx) => {
        const optHotels = opt.hotels || [];
        const optItinerary = buildItinerary(optHotels);
        const optPricing = getOptionPricing(idx);
        return {
          label: opt.label || `Option ${idx + 1}`,
          hotels: optHotels,
          itinerary: optItinerary,
          quote_per_adult: optPricing.adult,
          quote_per_cwb: optPricing.cwb,
          quote_per_cnb: optPricing.cnb,
          total_amount: optPricing.grandTotal
        };
      });
      
      // Use active option (first option with hotels, or first) for primary quote fields
      const primaryIdx = accommodationOptions.findIndex(o => o.hotels?.length > 0);
      const primaryOption = primaryIdx >= 0 ? primaryIdx : 0;
      const primaryPricing = getOptionPricing(primaryOption);
      const primaryItinerary = buildItinerary(accommodationOptions[primaryOption]?.hotels || []);
      
      const payload = {
        hotels: accommodationOptions[primaryOption]?.hotels || [],
        accommodation_options: optionsPayload,
        activities: selectedActivities,
        transfers_details: selectedTransfers,
        itinerary: primaryItinerary,
        cab_cost: taxiPricingMode === "manual" ? parseFloat(manualVehiclePrice || 0) : parseFloat(cabCost || 0),
        manual_vehicle_type: manualVehicleType || "",
        manual_vehicle_price: parseFloat(manualVehiclePrice || 0),
        manual_vehicle_count: parseInt(manualVehicleCount || 1, 10),
        manual_vehicle_ac: manualVehicleAc || "AC",
        manual_vehicle_custom_name: manualVehicleType === "Others" ? (manualVehicleCustomName || "") : "",
        entry_ticket_cost: entryTicketCost,
        ferry_cruise_cost: ferryCruiseCost,
        misc_cost: miscCost,
        quote_amount: primaryPricing.grandTotal,
        quote_per_adult: primaryPricing.adult,
        quote_per_cwb: primaryPricing.cwb,
        quote_per_cnb: primaryPricing.cnb,
        inclusions,
        exclusions,
        terms_conditions: termsConditions,
        show_terms_conditions: showTermsConditions,
      };
      // Only set status if the quote is NOT already in a terminal state.
      // Otherwise admin re-saving a quote (e.g., to fix a typo) would clobber
      // an agent-confirmed `booked` back to `quoted`.
      const TERMINAL = ['booked', 'confirmed', 'lost', 'cancelled'];
      if (!TERMINAL.includes(quote?.status)) {
        payload.status = "quoted";
      }
      
      await axios.put(`${API}/quotes/${id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Quote saved successfully!");
      navigate("/admin/quotes");
    } catch (error) {
      console.error("Error saving quote:", error);
      const errorMsg = error.response?.data?.detail || error.message || "Failed to save quote";
      toast.error(errorMsg);
    }
  };

  if (loading) return <div className="p-8 text-center text-gray-800">Loading...</div>;

  const tripDates = getDatesInRange(quote?.travel_date, quote?.return_date);

  // --- Smart Filtering Logic for Sightseeing ---
  // Supports combo destinations like "Assam, Meghalaya", "Assam & Meghalaya",
  // "Assam + Meghalaya", "Assam / Meghalaya", "Assam and Meghalaya".
  const getRelevantCities = () => {
      if (!quote?.destination) return [];

      // Split combo destinations on common separators
      const splitTokens = (str) =>
          (str || "")
              .split(/\s*(?:,|&|\+|\/|\band\b)\s*/i)
              .map(t => t.trim())
              .filter(Boolean);

      let cities = [quote.destination, ...splitTokens(quote.destination)];

      // For each token, check if it matches a known Destination Region and add its cities
      splitTokens(quote.destination).forEach(tok => {
          const destRecord = destinations.find(d =>
              d.name.toLowerCase() === tok.toLowerCase()
          );
          if (destRecord && destRecord.cities) {
              cities = [...cities, ...destRecord.cities];
          }
      });

      // Also: full-string match to a known region (legacy behavior)
      const fullDestRecord = destinations.find(d =>
          d.name.toLowerCase() === quote.destination.toLowerCase()
      );
      if (fullDestRecord && fullDestRecord.cities) {
          cities = [...cities, ...fullDestRecord.cities];
      }

      // Also check covered_cities field from quote request (supports same separators)
      if (quote.covered_cities) {
          cities = [...cities, ...splitTokens(quote.covered_cities)];
      }

      // De-duplicate
      return [...new Set(cities.map(c => c.toLowerCase()))];
  };

  const relevantCities = getRelevantCities();
  
  const filteredSightseeing = sightseeing.filter(s => {
      const searchMatch = s.tour_name.toLowerCase().includes(activitySearch.toLowerCase()) ||
                          s.destination.toLowerCase().includes(activitySearch.toLowerCase());
      
      // Strict Destination Match: Activity destination must be in relevant cities list
      // BUT if search term is active, allow searching outside? 
      // User said "SHOW ONLY THOSE...". So we strict filter.
      // But if user wants to search "Dubai" inside "Kerala" quote? Probably not.
      // Let's keep strict filter but relax if no relevant cities found (fallback to search only).
      
      if (relevantCities.length > 0) {
          const locationMatch = relevantCities.some(city => s.destination.toLowerCase().includes(city));
          return locationMatch && searchMatch;
      }
      
      return searchMatch;
  });

  return (
    <DashboardLayout type="admin">
      <div className="space-y-6 max-w-6xl mx-auto pb-20">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Quote Builder</h1>
            <div className="flex items-center gap-2 flex-wrap mt-1">
              <p className="text-gray-500">Building for: {quote?.agent_company || quote?.agent_name || "Direct"}</p>
              {quote?.quote_number && (
                <span className="text-xs font-mono bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-md border border-emerald-200" data-testid="qb-quote-number">{quote.quote_number}</span>
              )}
              <Button
                onClick={() => setShowEnquiryEdit(true)}
                variant="outline"
                size="sm"
                className="h-7 gap-1 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                data-testid="qb-edit-enquiry-btn"
              >
                <Edit className="w-3 h-3" /> Edit Enquiry
              </Button>
            </div>
          </div>
          <div className="flex items-start gap-6">
            {quote?.agent_name && (
              <div className="text-right border-r border-gray-200 pr-6" data-testid="header-agent-card">
                <p className="text-[11px] text-gray-400 uppercase tracking-wider">Agent</p>
                <div className="flex items-center gap-2 mt-1">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-xs font-bold">
                    {(quote.agent_name || '').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase() || 'AG'}
                  </div>
                  <div className="text-left">
                    <div className="text-sm font-bold text-gray-900">{quote.agent_name}</div>
                    {quote.agent_phone && <div className="text-[11px] text-gray-500">{quote.agent_phone}</div>}
                  </div>
                </div>
              </div>
            )}
            <div className="text-right">
              <p className="text-sm text-gray-500">Total Cost</p>
              <p className="text-2xl font-bold text-green-400">₹{getGrandTotal().toLocaleString()}</p>
            </div>
          </div>
        </div>

        {/* Revision Request Banner */}
        {quote && quote.status === 'revision_requested' && (
          <div className="bg-orange-50 border border-orange-300 rounded-xl p-4" data-testid="revision-banner">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-orange-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <RotateCcw className="w-4 h-4 text-orange-700" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-orange-800">Revision Requested</h3>
                  {quote.revision_history && quote.revision_history.length > 0 && (() => {
                    const latest = quote.revision_history[quote.revision_history.length - 1];
                    return (
                      <>
                        {latest.categories && latest.categories.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1 mb-1">
                            {latest.categories.map((cat, i) => (
                              <span key={i} className="text-[10px] bg-orange-100 border border-orange-300 rounded px-1.5 py-0.5 text-orange-700">{cat}</span>
                            ))}
                          </div>
                        )}
                        <p className="text-sm text-orange-700">{latest.notes}</p>
                        <p className="text-[10px] text-orange-500 mt-1">
                          By {latest.agent_name} on {formatDateDMY(latest.request_date)}
                          {' | '}Revision #{latest.revision_num}
                        </p>
                      </>
                    );
                  })()}
                </div>
              </div>
              <Button
                size="sm"
                className="bg-orange-600 hover:bg-orange-700 text-white flex-shrink-0"
                onClick={handleCompleteRevision}
                disabled={completingRevision}
                data-testid="complete-revision-btn"
              >
                <CheckCircle className="w-4 h-4 mr-1" />{completingRevision ? 'Completing...' : 'Complete Revision'}
              </Button>
            </div>
          </div>
        )}

        {/* Enquiry Details Card */}
        {quote && (
          <Card className="bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200 shadow-sm">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-emerald-800 text-lg flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Enquiry Details
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                <div>
                  <p className="text-xs text-emerald-600 font-medium">Guest Name</p>
                  <p className="text-gray-800 font-semibold text-base">{quote.client_name || "-"}</p>
                </div>
                <div>
                  <p className="text-xs text-emerald-600 font-medium">Destination</p>
                  <p className="text-gray-800 font-semibold text-base">{quote.destination || "-"}</p>
                </div>
                <div>
                  <p className="text-xs text-emerald-600 font-medium">Travel Date</p>
                  <p className="text-gray-800 font-semibold text-base">
                    {quote.travel_date ? formatDateDMY(quote.travel_date) : "-"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-emerald-600 font-medium">Return Date</p>
                  <p className="text-gray-800 font-semibold text-base">
                    {quote.return_date ? formatDateDMY(quote.return_date) : "-"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-emerald-600 font-medium">Travelers</p>
                  <p className="text-gray-800 font-semibold text-base">
                    {quote.pax_adults || 0} Adults{(quote.cwb || 0) > 0 ? `, ${quote.cwb} CWB` : ''}{(quote.cnb || 0) > 0 ? `, ${quote.cnb} CNB` : ''}{(quote.pax_infants || 0) > 0 ? `, ${quote.pax_infants} Infants` : ''}
                  </p>
                  {/* Child ages */}
                  {quote.cwb_ages && quote.cwb_ages.length > 0 && (
                    <p className="text-xs text-gray-500 mt-0.5">CWB Ages: {Array.isArray(quote.cwb_ages) ? quote.cwb_ages.join(', ') : quote.cwb_ages} yrs</p>
                  )}
                  {quote.cnb_ages && quote.cnb_ages.length > 0 && (
                    <p className="text-xs text-gray-500 mt-0.5">CNB Ages: {Array.isArray(quote.cnb_ages) ? quote.cnb_ages.join(', ') : quote.cnb_ages} yrs</p>
                  )}
                  {quote.infant_ages && quote.infant_ages.length > 0 && (
                    <p className="text-xs text-gray-500 mt-0.5">Infant Ages: {Array.isArray(quote.infant_ages) ? quote.infant_ages.join(', ') : quote.infant_ages} yrs</p>
                  )}
                </div>
                <div>
                  <p className="text-xs text-emerald-600 font-medium">Nights</p>
                  <p className="text-gray-800 font-semibold text-base">{quote.nights || getDatesInRange(quote.travel_date, quote.return_date).length - 1 || 0} Nights</p>
                </div>
              </div>
              
              {/* Additional Details Row */}
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mt-4 pt-4 border-t border-emerald-200">
                <div>
                  <p className="text-xs text-emerald-600 font-medium">Trip Type</p>
                  <p className="text-gray-800 font-semibold text-base capitalize">{quote.trip_type || "-"}</p>
                </div>
                <div>
                  <p className="text-xs text-emerald-600 font-medium">Hotel Category</p>
                  <p className="text-gray-800 font-semibold text-base">{quote.hotel_category || "-"}</p>
                  {quote.meal_plan && <p className="text-gray-500 text-sm">{quote.meal_plan.toUpperCase()}</p>}
                </div>
                <div>
                  <p className="text-xs text-emerald-600 font-medium">Covered Cities</p>
                  <p className="text-gray-800 font-semibold text-base">{quote.covered_cities || "-"}</p>
                </div>
                <div>
                  <p className="text-xs text-emerald-600 font-medium">Pick Up</p>
                  <p className="text-gray-800 font-semibold text-base">{quote.pickup_location || "-"}</p>
                </div>
                <div>
                  <p className="text-xs text-emerald-600 font-medium">Drop</p>
                  <p className="text-gray-800 font-semibold text-base">{quote.drop_location || "-"}</p>
                </div>
                <div>
                  <p className="text-xs text-emerald-600 font-medium">With Hotel</p>
                  {quote.with_hotel === "no" ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold mt-1" data-testid="with-hotel-badge">
                      No — Land Only
                    </span>
                  ) : (
                    <p className="text-gray-800 font-semibold text-base">Yes</p>
                  )}
                </div>
              </div>

              {quote.with_hotel === "no" && (
                <div className="mt-4 pt-4 border-t border-amber-200" data-testid="land-only-notice">
                  <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                    <span className="text-amber-700 font-semibold">⚠ Land-Only Quote —</span>
                    <span className="text-amber-800">The agent explicitly requested this quote <strong>without hotel</strong>. Please skip hotel selection and provide costs for transport, sightseeing, and meals only.</span>
                  </div>
                </div>
              )}

              {/* Special Requirements */}
              {quote.special_requirements && (
                <div className="mt-4 pt-4 border-t border-emerald-200">
                  <p className="text-xs text-emerald-600 font-medium mb-1">Special Requirements</p>
                  <p className="text-gray-700 text-sm bg-white/50 p-2 rounded">{quote.special_requirements}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step Progress Indicator */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-8">
              {/* Step 1 */}
              <div className={`flex items-center gap-2 ${currentStep >= 1 ? 'text-emerald-600' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${currentStep >= 1 ? 'bg-emerald-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
                  {currentStep > 1 ? '✓' : '1'}
                </div>
                <span className="font-medium">Accommodation</span>
              </div>
              <div className={`w-16 h-0.5 ${currentStep >= 2 ? 'bg-emerald-600' : 'bg-gray-200'}`}></div>
              
              {/* Step 2 */}
              <div className={`flex items-center gap-2 ${currentStep >= 2 ? 'text-emerald-600' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${currentStep >= 2 ? 'bg-emerald-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
                  {currentStep > 2 ? '✓' : '2'}
                </div>
                <span className="font-medium">Activities</span>
              </div>
              <div className={`w-16 h-0.5 ${currentStep >= 3 ? 'bg-emerald-600' : 'bg-gray-200'}`}></div>
              
              {/* Step 3 */}
              <div className={`flex items-center gap-2 ${currentStep >= 3 ? 'text-emerald-600' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${currentStep >= 3 ? 'bg-emerald-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
                  3
                </div>
                <span className="font-medium">Pricing & Summary</span>
              </div>
            </div>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-gray-50">
            <TabsTrigger value="hotel" className="data-[state=active]:bg-primary-700 data-[state=active]:text-white" onClick={() => setCurrentStep(1)}>
              <Hotel className="w-4 h-4 mr-2" /> Accommodation
            </TabsTrigger>
            <TabsTrigger value="activity" className="data-[state=active]:bg-primary-700 data-[state=active]:text-white" onClick={() => setCurrentStep(2)}>
              <MapPin className="w-4 h-4 mr-2" /> Activities
            </TabsTrigger>
            <TabsTrigger value="transport" className="data-[state=active]:bg-primary-700 data-[state=active]:text-white" onClick={() => setCurrentStep(3)}>
              <Truck className="w-4 h-4 mr-2" /> Transport & Pricing
            </TabsTrigger>
          </TabsList>

          {/* HOTEL TAB */}
          <TabsContent value="hotel" className="space-y-6">
            {/* Accommodation Option Tabs */}
            <div className="flex items-center gap-2 flex-wrap" data-testid="accommodation-options-tabs">
              {accommodationOptions.map((opt, idx) => (
                <button
                  key={idx}
                  onClick={() => { setActiveOption(idx); setShowHotelForm(false); setEditingHotelIndex(null); }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium border transition-all ${
                    activeOption === idx
                      ? "bg-emerald-600 text-white border-emerald-600 shadow-sm"
                      : opt.hotels?.length > 0
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
                        : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50"
                  }`}
                  data-testid={`option-tab-${idx}`}
                >
                  {opt.label}
                </button>
              ))}
              {accommodationOptions.length < 5 && (
                <button
                  onClick={() => {
                    const newOpt = { label: `Option ${accommodationOptions.length + 1}`, hotels: [] };
                    setAccommodationOptions([...accommodationOptions, newOpt]);
                    setActiveOption(accommodationOptions.length);
                    setShowHotelForm(false);
                    toast.success(`Option ${accommodationOptions.length + 1} added`);
                  }}
                  className="px-3 py-2 rounded-lg text-sm border border-dashed border-gray-300 text-gray-400 hover:text-emerald-600 hover:border-emerald-300 transition-all"
                  data-testid="add-option-btn"
                >
                  <Plus className="w-4 h-4 inline mr-1" /> Add Option
                </button>
              )}
              {accommodationOptions.length > 1 && accommodationOptions[activeOption]?.hotels?.length === 0 && (
                <button
                  onClick={() => {
                    const updated = accommodationOptions.filter((_, i) => i !== activeOption);
                    setAccommodationOptions(updated);
                    setActiveOption(Math.min(activeOption, updated.length - 1));
                    toast.success("Option removed");
                  }}
                  className="px-3 py-2 rounded-lg text-xs border border-red-200 text-red-400 hover:bg-red-50 transition-all"
                  data-testid="remove-option-btn"
                >
                  <Trash2 className="w-3 h-3 inline mr-1" /> Remove
                </button>
              )}
            </div>

            <div className="text-xs text-gray-400 bg-gray-50 rounded-lg px-3 py-2">
              Building <span className="font-semibold text-emerald-600">{accommodationOptions[activeOption]?.label}</span>
              {quote?.hotel_options_count > 1 && (
                <span> &bull; Agent requested {quote.hotel_options_count} options</span>
              )}
            </div>

            {hotels.map((h, i) => (
              <Card key={i} className="bg-white border-gray-200 relative group">
                <CardContent className="p-4">
                  <div className="grid md:grid-cols-5 gap-4">
                    <div>
                        <p className="text-xs text-gray-500">City & Hotel</p>
                        <p className="text-gray-800 font-medium">{h.city}</p>
                        <p className="text-gray-800 text-sm">{h.hotel_name}</p>
                        {h.hotel_category && (
                          <span className="inline-block mt-1 text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">{h.hotel_category}</span>
                        )}
                    </div>
                    <div>
                        <p className="text-xs text-gray-500">Dates</p>
                        <p className="text-gray-800 text-sm">{formatDateDMY(h.check_in)} to {formatDateDMY(h.check_out)}</p>
                        <p className="text-gray-400 text-xs">{h.nights} Nights</p>
                    </div>
                    <div>
                        <p className="text-xs text-gray-500">Room Details</p>
                        <p className="text-gray-800 text-sm">{h.room_type} ({h.meal_plan})</p>
                        <p className="text-gray-400 text-xs">{h.rooms_count} Rooms</p>
                        {/* Show CWB/CNB info if enabled */}
                        {(h.cwb_toggle || h.cnb_toggle || h.extra_bed_toggle) && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {h.extra_bed_toggle && <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">EB: {h.extra_bed_count}</span>}
                            {h.cwb_toggle && <span className="text-xs bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded">CWB: {h.cwb_count}</span>}
                            {h.cnb_toggle && <span className="text-xs bg-amber-100 text-amber-600 px-1.5 py-0.5 rounded">CNB: {h.cnb_count}</span>}
                          </div>
                        )}
                    </div>
                    <div className="text-right">
                        <p className="text-xs text-gray-500">Cost</p>
                        <p className="text-green-600 font-bold text-lg">₹{h.total_cost.toLocaleString()}</p>
                    </div>
                    <div className="flex items-start justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => editHotel(i)} className="text-blue-500 hover:text-blue-700 hover:bg-blue-50 h-8 w-8 p-0">
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => removeHotel(i)} className="text-red-400 hover:text-red-500 hover:bg-red-50 h-8 w-8 p-0">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            <Button onClick={openAddHotelForm} className="w-full border-dashed border-gray-200 text-gray-500 hover:text-gray-900 hover:bg-gray-50" variant="outline">
                <Plus className="w-4 h-4 mr-2" /> Add Accommodation
            </Button>

            {showHotelForm && (
              <Card className={editingHotelIndex !== null ? "bg-blue-500/5 border-blue-500/20" : "bg-yellow-500/5 border-yellow-500/20"}>
                <CardHeader>
                    <CardTitle className="text-gray-800 text-lg">
                      {editingHotelIndex !== null ? "Edit Hotel Details" : "Add Hotel Details"}
                    </CardTitle>
                    {/* Smart Date Info Banner - only show when adding */}
                    {editingHotelIndex === null && (
                      <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                        <p className="text-blue-700">
                          <span className="font-medium">📅 Smart Dates:</span> Check-in auto-set to {hotels.length === 0 ? "enquiry travel date" : "previous hotel's check-out"}. 
                          Max check-out: {quote?.return_date ? formatDateDMY(quote.return_date) : "N/A"}
                        </p>
                      </div>
                    )}
                </CardHeader>
                <CardContent className="space-y-4">
                    {/* City & Hotel Selection */}
                    <div className="grid md:grid-cols-4 gap-4">
                        <div>
                          <Label>City Name</Label>
                          <Select 
                            value={hotelInputMode === "manual" ? "__manual__" : hotelForm.city} 
                            onValueChange={handleCitySelection}
                          >
                            <SelectTrigger className="bg-white border-gray-200 shadow-sm">
                              <SelectValue placeholder="Select city..." />
                            </SelectTrigger>
                            <SelectContent>
                              {availableCities.map(city => (
                                <SelectItem key={city} value={city}>{city}</SelectItem>
                              ))}
                              <SelectItem value="__manual__" className="text-orange-600 border-t mt-1 pt-1">
                                + Manual Entry
                              </SelectItem>
                            </SelectContent>
                          </Select>
                          {hotelInputMode === "manual" && (
                            <Input 
                              value={hotelForm.city} 
                              onChange={e => handleHotelChange('city', e.target.value)} 
                              className="bg-white border-gray-200 mt-2" 
                              placeholder="Enter city name"
                            />
                          )}
                        </div>
                        <div className="md:col-span-2">
                          <Label>Hotel Name</Label>
                          {hotelInputMode === "resource" && availableHotelsForCity.length > 0 ? (
                            <>
                              <Select 
                                value={selectedResourceHotel?.id || ""} 
                                onValueChange={handleHotelResourceSelection}
                              >
                                <SelectTrigger className="bg-white border-gray-200 shadow-sm">
                                  <SelectValue placeholder="Select hotel..." />
                                </SelectTrigger>
                                <SelectContent>
                                  {availableHotelsForCity.map(h => (
                                    <SelectItem key={h.id} value={h.id}>
                                      {h.name} ({h.category})
                                    </SelectItem>
                                  ))}
                                  <SelectItem value="__manual__" className="text-orange-600 border-t mt-1 pt-1">
                                    + Manual Entry
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                              {selectedResourceHotel && (
                                <p className="text-xs text-emerald-600 mt-1">
                                  ✓ Pricing auto-loaded from resources
                                </p>
                              )}
                            </>
                          ) : (
                            <Input 
                              value={hotelForm.hotel_name} 
                              onChange={e => handleHotelChange('hotel_name', e.target.value)} 
                              className="bg-white border-gray-200" 
                              placeholder="Enter hotel name"
                            />
                          )}
                        </div>
                        <div>
                            <Label>Hotel Category</Label>
                            <Select 
                              value={hotelForm.hotel_category} 
                              onValueChange={v => handleHotelChange('hotel_category', v)}
                              disabled={hotelInputMode === "resource" && !!selectedResourceHotel}
                            >
                                <SelectTrigger className={`bg-white border-gray-200 shadow-sm ${hotelInputMode === "resource" && selectedResourceHotel ? "opacity-60" : ""}`}><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="Budget">Budget</SelectItem>
                                    <SelectItem value="2 Star">2 Star</SelectItem>
                                    <SelectItem value="3 Star">3 Star</SelectItem>
                                    <SelectItem value="3 Star Deluxe">3 Star Deluxe</SelectItem>
                                    <SelectItem value="4 Star">4 Star</SelectItem>
                                    <SelectItem value="4 Star Deluxe">4 Star Deluxe</SelectItem>
                                    <SelectItem value="5 Star">5 Star</SelectItem>
                                    <SelectItem value="5 Star Luxury">5 Star Luxury</SelectItem>
                                    <SelectItem value="Luxury">Luxury</SelectItem>
                                    <SelectItem value="Heritage">Heritage</SelectItem>
                                    <SelectItem value="Boutique">Boutique</SelectItem>
                                    <SelectItem value="Resort">Resort</SelectItem>
                                </SelectContent>
                            </Select>
                            {hotelInputMode === "resource" && selectedResourceHotel && (
                              <p className="text-xs text-gray-400 mt-1">Auto-set from resource</p>
                            )}
                        </div>
                    </div>
                    <div className="grid md:grid-cols-3 gap-4">
                        <div>
                          <Label>Check In</Label>
                          <Input 
                            type="date" 
                            value={hotelForm.check_in} 
                            onChange={e => handleHotelChange('check_in', e.target.value)} 
                            min={getMinCheckInDate()}
                            max={getMaxCheckOutDate()}
                            className="bg-white border-gray-200" 
                          />
                        </div>
                        <div>
                          <Label>Check Out</Label>
                          <Input 
                            type="date" 
                            value={hotelForm.check_out} 
                            onChange={e => {
                              const maxDate = getMaxCheckOutDate();
                              if (maxDate && e.target.value > maxDate) {
                                toast.error(`Check-out cannot exceed enquiry return date (${formatDateDMY(quote.return_date)})`);
                                return;
                              }
                              handleHotelChange('check_out', e.target.value);
                            }} 
                            min={hotelForm.check_in}
                            max={getMaxCheckOutDate()}
                            className="bg-white border-gray-200" 
                          />
                        </div>
                        <div><Label>Nights</Label><div className="h-10 flex items-center px-3 bg-white border border-gray-200 rounded text-gray-800">{hotelForm.nights}</div></div>
                    </div>
                    <div className="grid md:grid-cols-3 gap-4">
                        <div>
                            <Label>Meal Plan</Label>
                            <Select value={hotelForm.meal_plan} onValueChange={handleMealPlanChange}>
                                <SelectTrigger className="bg-white border-gray-200 shadow-sm"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="EP">EP (Room Only)</SelectItem>
                                    <SelectItem value="CP">CP (Breakfast)</SelectItem>
                                    <SelectItem value="MAP">MAP (B+L/D)</SelectItem>
                                    <SelectItem value="AP">AP (All Meals)</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                          <Label>Room Type</Label>
                          {selectedResourceHotel && selectedResourceHotel.rate_periods?.[0]?.room_pricing?.length > 0 ? (
                            <Select value={hotelForm.room_type} onValueChange={v => {
                              handleHotelChange('room_type', v);
                              // Update price based on selected room type
                              const period = selectedResourceHotel.rate_periods[0];
                              const room = period.room_pricing.find(r => r.room_category === v);
                              if (room) {
                                const mp = hotelForm.meal_plan.toLowerCase();
                                handleHotelChange('room_price', room[mp] || room.cp || 0);
                              }
                            }}>
                              <SelectTrigger className="bg-white border-gray-200 shadow-sm"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {selectedResourceHotel.rate_periods[0].room_pricing.map(r => (
                                  <SelectItem key={r.room_category} value={r.room_category}>
                                    {r.room_category}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Input value={hotelForm.room_type} onChange={e => handleHotelChange('room_type', e.target.value)} className="bg-white border-gray-200" placeholder="e.g. Deluxe" />
                          )}
                        </div>
                        <div><Label>No of Rooms</Label><Input type="number" value={hotelForm.rooms_count} onChange={e => handleHotelChange('rooms_count', parseInt(e.target.value) || 1)} className="bg-white border-gray-200" /></div>
                    </div>
                    <div className="grid md:grid-cols-3 gap-4">
                        <div><Label>Room Price (Per Room Per Night)</Label><Input type="number" value={hotelForm.room_price} onChange={e => handleHotelChange('room_price', parseFloat(e.target.value))} className="bg-white border-gray-200" /></div>
                        <div className="md:col-span-2 flex items-center justify-end pt-6">
                            <p className="text-gray-500 text-sm mr-2">Base Cost:</p>
                            <p className="text-xl font-bold text-gray-800">₹{(hotelForm.rooms_count * hotelForm.room_price * hotelForm.nights).toLocaleString()}</p>
                        </div>
                    </div>

                    {/* Extra Bed - Always available for admin flexibility */}
                    <div className="p-3 bg-white/50 rounded border border-gray-200">
                        <div className="flex justify-between items-center mb-2">
                            <Label>Extra Bed</Label>
                            <Switch checked={hotelForm.extra_bed_toggle} onCheckedChange={c => handleHotelChange('extra_bed_toggle', c)} />
                        </div>
                        {hotelForm.extra_bed_toggle && (
                            <div className="grid grid-cols-2 gap-4">
                                <div><Label>Count</Label><Input type="number" value={hotelForm.extra_bed_count} onChange={e => handleHotelChange('extra_bed_count', parseInt(e.target.value) || 0)} className="bg-gray-50 border-gray-200" /></div>
                                <div><Label>Price (Per Night)</Label><Input type="number" value={hotelForm.extra_bed_price} onChange={e => handleHotelChange('extra_bed_price', parseFloat(e.target.value) || 0)} className="bg-gray-50 border-gray-200" /></div>
                            </div>
                        )}
                    </div>

                    {/* Child With Bed (CWB) - Enabled only if quote has CWB */}
                    <div className={`p-3 rounded border ${hasCwbInQuote() ? 'bg-blue-50/50 border-blue-200' : 'bg-gray-100 border-gray-200 opacity-60'}`}>
                        <div className="flex justify-between items-center mb-2">
                            <div className="flex items-center gap-2">
                              <Label className={hasCwbInQuote() ? "text-blue-700" : "text-gray-500"}>Child With Bed (CWB)</Label>
                              {!hasCwbInQuote() && <span className="text-xs text-gray-400">(No CWB in enquiry)</span>}
                            </div>
                            <Switch 
                              checked={hotelForm.cwb_toggle} 
                              onCheckedChange={c => handleHotelChange('cwb_toggle', c)} 
                              disabled={!hasCwbInQuote()}
                            />
                        </div>
                        {hotelForm.cwb_toggle && hasCwbInQuote() && (
                            <div className="grid grid-cols-2 gap-4">
                                <div><Label>Count</Label><Input type="number" value={hotelForm.cwb_count} onChange={e => handleHotelChange('cwb_count', parseInt(e.target.value) || 0)} className="bg-white border-gray-200" /></div>
                                <div><Label>Price (Per Night)</Label><Input type="number" value={hotelForm.cwb_price} onChange={e => handleHotelChange('cwb_price', parseFloat(e.target.value) || 0)} className="bg-white border-gray-200" /></div>
                            </div>
                        )}
                    </div>

                    {/* Child No Bed (CNB) - Enabled only if quote has CNB/children */}
                    <div className={`p-3 rounded border ${hasCnbInQuote() ? 'bg-amber-50/50 border-amber-200' : 'bg-gray-100 border-gray-200 opacity-60'}`}>
                        <div className="flex justify-between items-center mb-2">
                            <div className="flex items-center gap-2">
                              <Label className={hasCnbInQuote() ? "text-amber-700" : "text-gray-500"}>Child No Bed (CNB)</Label>
                              {!hasCnbInQuote() && <span className="text-xs text-gray-400">(No CNB/Children in enquiry)</span>}
                            </div>
                            <Switch 
                              checked={hotelForm.cnb_toggle} 
                              onCheckedChange={c => handleHotelChange('cnb_toggle', c)} 
                              disabled={!hasCnbInQuote()}
                            />
                        </div>
                        {hotelForm.cnb_toggle && hasCnbInQuote() && (
                            <div className="grid grid-cols-2 gap-4">
                                <div><Label>Count</Label><Input type="number" value={hotelForm.cnb_count} onChange={e => handleHotelChange('cnb_count', parseInt(e.target.value) || 0)} className="bg-white border-gray-200" /></div>
                                <div><Label>Price (Per Night)</Label><Input type="number" value={hotelForm.cnb_price} onChange={e => handleHotelChange('cnb_price', parseFloat(e.target.value) || 0)} className="bg-white border-gray-200" /></div>
                            </div>
                        )}
                    </div>

                    <div className="flex justify-between items-center pt-4 border-t border-yellow-500/20">
                        <p className="text-lg font-bold text-gray-800">Total: ₹{hotelForm.total_cost.toLocaleString()}</p>
                        <div className="flex gap-2">
                            <Button variant="ghost" onClick={() => { setShowHotelForm(false); setEditingHotelIndex(null); resetHotelFormState(); }}>Cancel</Button>
                            <Button onClick={addHotel} className={editingHotelIndex !== null ? "bg-blue-600 hover:bg-blue-700" : "bg-yellow-600 hover:bg-yellow-700 text-gray-800"}>
                              {editingHotelIndex !== null ? "Update Accommodation" : "Add Accommodation"}
                            </Button>
                        </div>
                    </div>
                </CardContent>
              </Card>
            )}

            {/* Save & Next Button for Accommodation */}
            {hotels.length > 0 && !showHotelForm && (
              <div className="flex justify-end">
                <Button 
                  onClick={saveAccommodationAndNext} 
                  disabled={savingStep}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-3 text-lg"
                  data-testid="save-accommodation-next-btn"
                >
                  {savingStep ? (
                    <>Saving...</>
                  ) : (
                    <>Save & Next <ChevronRight className="w-5 h-5 ml-2" /></>
                  )}
                </Button>
              </div>
            )}
          </TabsContent>

          {/* ACTIVITIES TAB */}
          <TabsContent value="activity" className="space-y-6">
            <Card className="bg-white border-gray-200 shadow-sm">
                <CardHeader><CardTitle className="text-gray-800">Day-wise Itinerary & Activities</CardTitle></CardHeader>
                <CardContent className="space-y-6">
                    {tripDates.length === 0 ? (
                        <p className="text-gray-500 text-center">Please define travel dates in quote details.</p>
                    ) : (
                        tripDates.map((dayObj) => (
                            <div key={dayObj.day} className="border-b border-gray-200 pb-6 last:border-0">
                                <h3 className="text-lg font-semibold text-emerald-600 mb-3">
                                    DAY {dayObj.day}: {dayObj.dateStr.toUpperCase()} - {dayObj.weekday.toUpperCase()}
                                </h3>
                                
                                <div className="space-y-4 mb-3">
                                    {selectedActivities.map((activity, idx) => {
                                      if (activity.day !== dayObj.day) return null;
                                      
                                      return (
                                        <div key={idx} className="p-4 bg-gray-50 rounded border border-gray-200">
                                            <div className="flex justify-between items-start mb-2">
                                                <div className="flex-1">
                                                    <h4 className="text-gray-800 font-medium">{activity.title}</h4>
                                                    {activity.description && (
                                                      <p className="text-gray-500 text-sm mt-1 line-clamp-2">{activity.description}</p>
                                                    )}
                                                    <div className="flex items-center gap-2 mt-1">
                                                        <p className="text-emerald-600 text-sm">₹{activity.price?.toLocaleString() || 0} (Adult)</p>
                                                        {activity.tour_type && (
                                                            <span
                                                                className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                                                                    activity.tour_type === "PVT" ? "bg-indigo-100 text-indigo-700"
                                                                    : activity.tour_type === "NONE" ? "bg-gray-200 text-gray-600"
                                                                    : "bg-amber-100 text-amber-700"
                                                                }`}
                                                                data-testid={`activity-tour-type-${idx}`}
                                                            >
                                                                {activity.tour_type === "PVT" ? "Private (PVT)"
                                                                    : activity.tour_type === "NONE" ? "No Transfer"
                                                                    : "Seat in Coach (SIC)"}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <div className="flex items-center gap-2 mr-4">
                                                        <Switch 
                                                            checked={activity.billable !== false} 
                                                            onCheckedChange={() => toggleActivityBillable(idx)} 
                                                        />
                                                        <span className="text-xs text-gray-600">Billable</span>
                                                    </div>
                                                    <Button variant="ghost" size="sm" onClick={() => openEditActivity(idx)} className="text-blue-600 hover:text-blue-700" data-testid={`edit-activity-${idx}`}>
                                                        <Edit className="w-4 h-4" />
                                                    </Button>
                                                    <Button variant="ghost" size="sm" onClick={() => removeActivity(idx)} className="text-red-400 hover:text-red-300">
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                            
                                            {/* Image Section */}
                                            <div className="flex items-center gap-4 mt-3">
                                                {activity.custom_image ? (
                                                    <div className="relative group w-24 h-16 rounded overflow-hidden">
                                                        <img src={activity.custom_image} alt="" className="w-full h-full object-cover" />
                                                        <button 
                                                            onClick={() => {
                                                                const newActs = [...selectedActivities];
                                                                newActs[idx].custom_image = "";
                                                                setSelectedActivities(newActs);
                                                            }}
                                                            className="absolute top-0 right-0 bg-black/50 p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                                        >
                                                            <X className="w-3 h-3 text-gray-800" />
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div className="flex gap-2">
                                                        <Button variant="outline" size="sm" onClick={() => generateActivityImage(idx)} className="text-xs h-8 border-slate-600 text-gray-600">
                                                            <Sparkles className="w-3 h-3 mr-1" /> AI Image
                                                        </Button>
                                                        <div className="relative">
                                                            <Button variant="outline" size="sm" className="text-xs h-8 border-slate-600 text-gray-600">
                                                                <Camera className="w-3 h-3 mr-1" /> Upload
                                                            </Button>
                                                            <input 
                                                                type="file" 
                                                                accept="image/*" 
                                                                className="absolute inset-0 opacity-0 cursor-pointer"
                                                                onChange={(e) => handleImageUpload(e, idx)}
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                      );
                                    })}
                                </div>

                                <div className="flex flex-wrap gap-2">
                                  <Button 
                                      size="sm" 
                                      variant="outline" 
                                      className="border-gray-200 text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                                      onClick={() => openActivityModal(dayObj.day)}
                                  >
                                      <Plus className="w-4 h-4 mr-2" /> ADD ACTIVITY
                                  </Button>
                                  <Button 
                                      size="sm" 
                                      variant="outline" 
                                      className="border-amber-200 text-amber-600 hover:text-amber-900 hover:bg-amber-50"
                                      onClick={() => { setCurrentDayForActivity(dayObj.day); setShowEntryTicketModal(true); }}
                                      data-testid={`add-entry-ticket-day-${dayObj.day}`}
                                  >
                                      <Plus className="w-4 h-4 mr-2" /> ENTRY TICKET
                                  </Button>
                                  <Button 
                                      size="sm" 
                                      variant="outline" 
                                      className="border-purple-200 text-purple-600 hover:text-purple-900 hover:bg-purple-50"
                                      onClick={() => { setCurrentDayForActivity(dayObj.day); setShowManualActivityModal(true); }}
                                      data-testid={`add-manual-day-${dayObj.day}`}
                                  >
                                      <Plus className="w-4 h-4 mr-2" /> MANUAL
                                  </Button>
                                </div>
                            </div>
                        ))
                    )}
                </CardContent>
            </Card>

            {/* INCLUSIONS & EXCLUSIONS */}
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-gray-800">Inclusions & Exclusions</CardTitle>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={loadTemplateFromResources}
                    disabled={loadingTemplate}
                    className="border-purple-300 text-purple-600 hover:bg-purple-50"
                    data-testid="load-template-button"
                  >
                    {loadingTemplate ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600 mr-2" />
                    ) : (
                      <Copy className="w-4 h-4 mr-2" />
                    )}
                    Load from Template
                  </Button>
                </div>
                {templateLoaded && (
                  <div className="mt-2 p-2 bg-purple-50 border border-purple-200 rounded-lg text-sm">
                    <p className="text-purple-700">
                      <CheckCircle className="w-4 h-4 inline mr-1" />
                      Loaded from <strong>{quote?.destination}</strong> template. Feel free to edit as needed.
                    </p>
                  </div>
                )}
                {!templateLoaded && !inclusions && !exclusions && (
                  <p className="text-sm text-gray-500 mt-1">
                    Click "Load from Template" to auto-fill from {quote?.destination || "destination"} template, or enter manually.
                  </p>
                )}
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Inclusions */}
                  <div>
                    <Label className="flex items-center gap-2 text-emerald-700 mb-2">
                      <CheckSquare className="w-4 h-4" />
                      Inclusions
                    </Label>
                    <Textarea
                      value={inclusions}
                      onChange={(e) => { setInclusions(e.target.value); setTemplateLoaded(false); }}
                      placeholder="Enter what's included in the package...

Example:
• All sightseeing as per itinerary
• Accommodation on twin/double sharing basis
• Daily breakfast at the hotel
• Airport/Railway station transfers
• All applicable taxes"
                      className="min-h-[200px] bg-emerald-50/30 border-emerald-200 text-gray-800 placeholder:text-gray-400"
                      data-testid="quote-inclusions-textarea"
                    />
                  </div>

                  {/* Exclusions */}
                  <div>
                    <Label className="flex items-center gap-2 text-red-600 mb-2">
                      <XSquare className="w-4 h-4" />
                      Exclusions
                    </Label>
                    <Textarea
                      value={exclusions}
                      onChange={(e) => { setExclusions(e.target.value); setTemplateLoaded(false); }}
                      placeholder="Enter what's NOT included in the package...

Example:
• Airfare / Train fare
• Any meals not mentioned
• Personal expenses like tips, laundry
• Travel insurance
• Entry fees to monuments (if applicable)"
                      className="min-h-[200px] bg-red-50/30 border-red-200 text-gray-800 placeholder:text-gray-400"
                      data-testid="quote-exclusions-textarea"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-3">
                  Tip: Use bullet points (•) or new lines for better readability. Content is editable even after loading from template.
                </p>
              </CardContent>
            </Card>

            {/* TERMS & CONDITIONS */}
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-gray-800">
                    <ScrollText className="w-5 h-5 text-indigo-600" />
                    Terms & Conditions
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <span className={`text-sm ${showTermsConditions ? 'text-indigo-600 font-medium' : 'text-gray-400'}`}>
                      {showTermsConditions ? 'Enabled' : 'Disabled'}
                    </span>
                    <Switch
                      checked={showTermsConditions}
                      onCheckedChange={setShowTermsConditions}
                      data-testid="terms-conditions-toggle"
                    />
                  </div>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  {showTermsConditions ? 'Terms & Conditions will be included in the quote.' : 'Toggle on to add Terms & Conditions to this quote.'}
                </p>
              </CardHeader>
              {showTermsConditions && (
                <CardContent>
                  <Textarea
                    value={termsConditions}
                    onChange={(e) => setTermsConditions(e.target.value)}
                    placeholder="Enter terms and conditions...

Example:
• 50% advance payment required at the time of booking confirmation
• Balance payment due 15 days before travel date
• Cancellation charges apply as per company policy
• Room allocation is subject to availability at the time of booking
• Check-in time: 2:00 PM | Check-out time: 12:00 PM
• Any increase in government taxes will be charged extra
• In case of non-availability of mentioned hotels, alternate similar category hotels will be provided"
                    className="min-h-[200px] bg-indigo-50/30 border-indigo-200 text-gray-800 placeholder:text-gray-400"
                    data-testid="terms-conditions-textarea"
                  />
                  <p className="text-xs text-gray-500 mt-3">
                    Use bullet points (•) or new lines for better readability.
                  </p>
                </CardContent>
              )}
            </Card>

            {/* Save & Next Button for Activities */}
            <div className="flex justify-between">
              <Button 
                onClick={goToPreviousStep} 
                variant="outline"
                className="px-6"
                data-testid="back-to-accommodation-btn"
              >
                <ChevronLeft className="w-5 h-5 mr-2" /> Back to Accommodation
              </Button>
              <Button 
                onClick={saveActivitiesAndNext} 
                disabled={savingStep}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-3 text-lg"
                data-testid="save-activities-next-btn"
              >
                {savingStep ? (
                  <>Saving...</>
                ) : (
                  <>Save & Next <ChevronRight className="w-5 h-5 ml-2" /></>
                )}
              </Button>
            </div>
          </TabsContent>

          {/* TRANSPORT & PRICING TAB */}
          <TabsContent value="transport" className="space-y-6">
            <Card className="bg-white border-gray-200 shadow-sm">
                <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2">
                        <Car className="w-5 h-5 text-emerald-600" />
                        Taxi / Transport Pricing
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                    {/* Toggle for Manual vs Auto */}
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <div>
                            <h4 className="font-medium text-gray-800">Pricing Mode</h4>
                            <p className="text-sm text-gray-500">
                                {taxiPricingMode === "auto" 
                                    ? `Auto-fetch pricing for ${quote?.destination || 'Unknown'}${calculateNights() ? ` - ${calculateNights()}N` : ''}` 
                                    : "Enter taxi cost manually"}
                            </p>
                        </div>
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => handleTaxiModeChange("manual")}
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                    taxiPricingMode === "manual" 
                                        ? "bg-emerald-600 text-white" 
                                        : "bg-gray-200 text-gray-600 hover:bg-gray-300"
                                }`}
                            >
                                Manual
                            </button>
                            <button
                                onClick={() => handleTaxiModeChange("auto")}
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                    taxiPricingMode === "auto" 
                                        ? "bg-emerald-600 text-white" 
                                        : "bg-gray-200 text-gray-600 hover:bg-gray-300"
                                }`}
                            >
                                Automatic
                            </button>
                        </div>
                    </div>

                    {/* Auto Pricing Section */}
                    {taxiPricingMode === "auto" && (
                        <div className="space-y-4">
                            {loadingTaxiPricing ? (
                                <div className="flex items-center justify-center py-8">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
                                    <span className="ml-3 text-gray-500">Fetching pricing...</span>
                                </div>
                            ) : autoTaxiPricing ? (
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between">
                                        <h4 className="font-medium text-gray-800">
                                            Available Vehicles for {autoTaxiPricing.destination} - {autoTaxiPricing.nights}N
                                        </h4>
                                        <span className="text-xs text-gray-500">Click to select</span>
                                    </div>
                                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                                        {autoTaxiPricing.vehicles.map((vehicle, idx) => (
                                            <div 
                                                key={idx}
                                                onClick={() => selectAutoVehicle(vehicle)}
                                                className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                                                    selectedVehicle?.vehicle_type === vehicle.vehicle_type
                                                        ? "border-emerald-500 bg-emerald-50 shadow-md"
                                                        : "border-gray-200 bg-white hover:border-emerald-300 hover:bg-gray-50"
                                                }`}
                                            >
                                                <div className="flex items-center gap-2 mb-2">
                                                    <Car className={`w-5 h-5 ${
                                                        selectedVehicle?.vehicle_type === vehicle.vehicle_type
                                                            ? "text-emerald-600"
                                                            : "text-gray-400"
                                                    }`} />
                                                    <span className="font-medium text-gray-800 text-sm">{vehicle.vehicle_type}</span>
                                                </div>
                                                <p className={`text-xl font-bold ${
                                                    selectedVehicle?.vehicle_type === vehicle.vehicle_type
                                                        ? "text-emerald-600"
                                                        : "text-gray-800"
                                                }`}>
                                                    ₹{vehicle.price.toLocaleString()}
                                                </p>
                                                {selectedVehicle?.vehicle_type === vehicle.vehicle_type && (
                                                    <div className="flex items-center gap-1 mt-2 text-emerald-600 text-xs">
                                                        <CheckCircle className="w-4 h-4" />
                                                        Selected
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-8 bg-amber-50 rounded-lg border border-amber-200">
                                    <Car className="w-12 h-12 text-amber-400 mx-auto mb-3" />
                                    <p className="text-amber-700 font-medium">No automatic pricing found</p>
                                    <p className="text-amber-600 text-sm mt-1">
                                        No pricing configured for {quote?.destination || 'Unknown'}{calculateNights() ? ` - ${calculateNights()}N` : ''}
                                    </p>
                                    <p className="text-gray-500 text-sm mt-2">
                                        Switch to Manual mode or add pricing in Resource Manager → Taxi Pricing
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Manual Pricing Section */}
                    {taxiPricingMode === "manual" && (
                        <div className="space-y-4">
                            <div className="grid md:grid-cols-2 gap-6">
                                <div>
                                    <Label>Vehicle Type</Label>
                                    <select
                                        value={manualVehicleType}
                                        onChange={(e) => setManualVehicleType(e.target.value)}
                                        className="w-full mt-1 h-10 rounded-md border border-gray-200 bg-gray-50 px-3 text-sm"
                                        data-testid="manual-vehicle-type"
                                    >
                                        <option value="">Select Vehicle</option>
                                        <option value="Sedan">Sedan</option>
                                        <option value="Ertiga">Ertiga</option>
                                        <option value="Innova">Innova</option>
                                        <option value="Innova Crysta">Innova Crysta</option>
                                        <option value="Tempo Traveller 12 Seater">Tempo Traveller 12 Seater</option>
                                        <option value="Tempo Traveller 17 Seater">Tempo Traveller 17 Seater</option>
                                        <option value="Mini Bus 20 Seater">Mini Bus 20 Seater</option>
                                        <option value="Bus 35 Seater">Bus 35 Seater</option>
                                        <option value="Bus 45 Seater">Bus 45 Seater</option>
                                        <option value="Others">Others (Manual)</option>
                                    </select>
                                    {/* Manual vehicle name input when "Others" selected */}
                                    {manualVehicleType === "Others" && (
                                        <Input
                                            type="text"
                                            value={manualVehicleCustomName}
                                            onChange={(e) => setManualVehicleCustomName(e.target.value)}
                                            placeholder="Enter vehicle name (e.g., Xylo)"
                                            className="bg-amber-50 border-amber-300 mt-2"
                                            data-testid="manual-vehicle-custom-name"
                                        />
                                    )}
                                </div>
                                <div>
                                    <Label>Vehicle Cost</Label>
                                    <Input 
                                        type="number" 
                                        value={manualVehiclePrice} 
                                        onChange={(e) => setManualVehiclePrice(parseFloat(e.target.value) || 0)} 
                                        className="bg-gray-50 border-gray-200 mt-1"
                                        placeholder="0"
                                        data-testid="manual-vehicle-price"
                                    />
                                </div>
                            </div>
                            <div className="grid md:grid-cols-2 gap-6">
                                <div>
                                    <Label>No of Vehicles</Label>
                                    <select
                                        value={manualVehicleCount}
                                        onChange={(e) => setManualVehicleCount(parseInt(e.target.value, 10) || 1)}
                                        className="w-full mt-1 h-10 rounded-md border border-gray-200 bg-gray-50 px-3 text-sm"
                                        data-testid="manual-vehicle-count"
                                    >
                                        {[1,2,3,4,5,6,7,8,9,10].map(n => (
                                            <option key={n} value={n}>{n}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <Label>AC / Non-AC</Label>
                                    <select
                                        value={manualVehicleAc}
                                        onChange={(e) => setManualVehicleAc(e.target.value)}
                                        className="w-full mt-1 h-10 rounded-md border border-gray-200 bg-gray-50 px-3 text-sm"
                                        data-testid="manual-vehicle-ac"
                                    >
                                        <option value="AC">AC</option>
                                        <option value="Non-AC">Non-AC</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Selected Vehicle Display (Auto mode) */}
                    {taxiPricingMode === "auto" && selectedVehicle && (
                        <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <Car className="w-6 h-6 text-emerald-600" />
                                    <div>
                                        <p className="font-medium text-gray-800">Selected: {selectedVehicle.vehicle_type}</p>
                                        <p className="text-sm text-gray-500">
                                            {autoTaxiPricing?.destination} - {autoTaxiPricing?.nights} Nights
                                        </p>
                                    </div>
                                </div>
                                <p className="text-2xl font-bold text-emerald-600">₹{selectedVehicle.price.toLocaleString()}</p>
                            </div>
                        </div>
                    )}

                    {/* Markup (for auto mode too) */}
                    {taxiPricingMode === "auto" && (
                        <div className="grid md:grid-cols-2 gap-6">
                            <div>
                                <Label>Additional Markup</Label>
                                <Input 
                                    type="number" 
                                    value={markup} 
                                    onChange={(e) => setMarkup(parseFloat(e.target.value) || 0)} 
                                    className="bg-gray-50 border-gray-200 mt-1"
                                    placeholder="0"
                                />
                            </div>
                        </div>
                    )}
            </CardContent>
          </Card>

          {/* COMPREHENSIVE PRICING BREAKDOWN */}
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Calculator className="w-5 h-5 text-emerald-600" />
                  Cost Breakdown (Per Person)
                </CardTitle>
                {/* Markup Mode Toggle */}
                <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => setMarkupMode("manual")}
                    className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                      markupMode === "manual" 
                        ? "bg-white text-emerald-700 shadow-sm" 
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    Manual Markup
                  </button>
                  <button
                    onClick={() => setMarkupMode("percentage")}
                    className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                      markupMode === "percentage" 
                        ? "bg-white text-emerald-700 shadow-sm" 
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    % Markup
                  </button>
                </div>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Travelers: {getAdultCount()} Adults{getCwbCount() > 0 ? `, ${getCwbCount()} CWB` : ""}{getCnbCount() > 0 ? `, ${getCnbCount()} CNB` : ""}
              </p>
            </CardHeader>
            <CardContent>
              {/* Compare All Options — visible only when agent requested multiple options */}
              {accommodationOptions.length > 1 && (
                <div className="mb-6 bg-gradient-to-r from-emerald-50 to-white border border-emerald-200 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">Compare All Options</span>
                      <span className="text-[10px] text-gray-400">({accommodationOptions.length} options)</span>
                    </div>
                    <span className="text-[11px] text-gray-500">
                      Detailed breakdown below is for <strong className="text-emerald-700">{accommodationOptions[activeOption]?.label || `Option ${activeOption + 1}`}</strong>
                    </span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                    {accommodationOptions.map((opt, idx) => {
                      const pricing = getOptionPricing(idx);
                      const hotelCount = (opt.hotels || []).length;
                      const isActive = idx === activeOption;
                      return (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => setActiveOption(idx)}
                          className={`text-left bg-white rounded-lg p-3 transition-all cursor-pointer ${
                            isActive 
                              ? "border-2 border-emerald-500 shadow-md" 
                              : "border border-gray-200 hover:border-emerald-300 hover:shadow-sm"
                          }`}
                          data-testid={`compare-option-${idx}`}
                        >
                          <div className="flex items-center justify-between">
                            <span className={`text-xs font-bold ${isActive ? 'text-emerald-700' : 'text-gray-600'}`}>
                              {opt.label || `Option ${idx + 1}`}
                            </span>
                            {isActive && <span className="text-[9px] bg-emerald-600 text-white px-1.5 py-0.5 rounded-full font-bold">ACTIVE</span>}
                          </div>
                          <div className="mt-2 space-y-0.5 text-[11px]">
                            <div className="flex justify-between">
                              <span className="text-gray-500">Per Adult</span>
                              <span className="font-mono font-semibold text-gray-800">₹{pricing.adult.toLocaleString()}</span>
                            </div>
                            {getCwbCount() > 0 && (
                              <div className="flex justify-between">
                                <span className="text-gray-500">CWB</span>
                                <span className="font-mono text-blue-600">₹{pricing.cwb.toLocaleString()}</span>
                              </div>
                            )}
                            {getCnbCount() > 0 && (
                              <div className="flex justify-between">
                                <span className="text-gray-500">CNB</span>
                                <span className="font-mono text-amber-600">₹{pricing.cnb.toLocaleString()}</span>
                              </div>
                            )}
                          </div>
                          <div className="mt-2 pt-2 border-t border-gray-100 flex justify-between items-center">
                            <span className="text-[10px] text-gray-400 uppercase">Total</span>
                            <span className="text-sm font-bold text-emerald-700 font-mono">₹{pricing.grandTotal.toLocaleString()}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Cost Category</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Per Adult</th>
                      <th className={`text-right py-3 px-4 font-semibold border-b border-gray-200 ${hasCwbInQuote() ? 'text-blue-700' : 'text-gray-300'}`}>CWB {!hasCwbInQuote() && <span className="text-[10px] font-normal block">(N/A)</span>}</th>
                      <th className={`text-right py-3 px-4 font-semibold border-b border-gray-200 ${hasCnbInQuote() ? 'text-amber-700' : 'text-gray-300'}`}>CNB {!hasCnbInQuote() && <span className="text-[10px] font-normal block">(N/A)</span>}</th>
                      <th className="text-right py-3 px-4 font-semibold text-emerald-700 border-b border-gray-200">Grand Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Accommodation Cost */}
                    <tr className="hover:bg-gray-50/50">
                      <td className="py-3 px-4 border-b border-gray-100 font-medium text-gray-700">Accommodation Cost</td>
                      <td className="py-3 px-4 border-b border-gray-100 text-right font-mono">₹{getAccommodationPerPerson().adult.toLocaleString()}</td>
                      <td className={`py-3 px-4 border-b border-gray-100 text-right font-mono ${hasCwbInQuote() ? 'text-blue-600' : 'text-gray-300'}`}>{hasCwbInQuote() ? `₹${getAccommodationPerPerson().cwb.toLocaleString()}` : '-'}</td>
                      <td className={`py-3 px-4 border-b border-gray-100 text-right font-mono ${hasCnbInQuote() ? 'text-amber-600' : 'text-gray-300'}`}>{hasCnbInQuote() ? `₹${getAccommodationPerPerson().cnb.toLocaleString()}` : '-'}</td>
                      <td className="py-3 px-4 border-b border-gray-100 text-right font-mono font-semibold">₹{getTotalHotelCost().toLocaleString()}</td>
                    </tr>

                    {/* Extra Bed Cost — informational only; already counted inside Accommodation
                        Cost above, surfaced here so toggling it is visibly reflected somewhere. */}
                    {getExtraBedTotal() > 0 && (
                      <tr className="hover:bg-gray-50/50 bg-gray-50/30">
                        <td className="py-3 px-4 border-b border-gray-100 text-gray-500 text-sm">
                          ↳ incl. Extra Bed
                        </td>
                        <td className="py-3 px-4 border-b border-gray-100 text-right font-mono text-sm text-gray-500" colSpan={3}></td>
                        <td className="py-3 px-4 border-b border-gray-100 text-right font-mono text-sm text-gray-500" data-testid="extra-bed-cost-total">
                          ₹{getExtraBedTotal().toLocaleString()}
                        </td>
                      </tr>
                    )}

                    {/* Activities Cost */}
                    <tr className="hover:bg-gray-50/50">
                      <td className="py-3 px-4 border-b border-gray-100 font-medium text-gray-700">Activities Cost</td>
                      <td className="py-3 px-4 border-b border-gray-100 text-right font-mono">₹{getActivitiesPerPerson().adult.toLocaleString()}</td>
                      <td className={`py-3 px-4 border-b border-gray-100 text-right font-mono ${hasCwbInQuote() ? 'text-blue-600' : 'text-gray-300'}`}>{hasCwbInQuote() ? `₹${getActivitiesPerPerson().cwb.toLocaleString()}` : '-'}</td>
                      <td className={`py-3 px-4 border-b border-gray-100 text-right font-mono ${hasCnbInQuote() ? 'text-amber-600' : 'text-gray-300'}`}>{hasCnbInQuote() ? `₹${getActivitiesPerPerson().cnb.toLocaleString()}` : '-'}</td>
                      <td className="py-3 px-4 border-b border-gray-100 text-right font-mono font-semibold">
                        ₹{((getActivitiesPerPerson().adult * getAdultCount()) + (hasCwbInQuote() ? getActivitiesPerPerson().cwb * getCwbCount() : 0) + (hasCnbInQuote() ? getActivitiesPerPerson().cnb * getCnbCount() : 0)).toLocaleString()}
                      </td>
                    </tr>

                    {/* Entry Ticket Cost */}
                    <tr className="hover:bg-gray-50/50">
                      <td className="py-3 px-4 border-b border-gray-100 font-medium text-gray-700">Entry Ticket Cost</td>
                      <td className="py-3 px-4 border-b border-gray-100 text-right">
                        <Input type="number" value={entryTicketCost.adult} onChange={e => setEntryTicketCost({...entryTicketCost, adult: parseFloat(e.target.value) || 0})} className="w-24 h-8 text-right bg-gray-50 border-gray-200 ml-auto" />
                      </td>
                      <td className="py-3 px-4 border-b border-gray-100 text-right">
                        {hasCwbInQuote() ? <Input type="number" value={entryTicketCost.cwb} onChange={e => setEntryTicketCost({...entryTicketCost, cwb: parseFloat(e.target.value) || 0})} className="w-24 h-8 text-right bg-blue-50 border-blue-200 ml-auto" /> : <span className="text-gray-300">-</span>}
                      </td>
                      <td className="py-3 px-4 border-b border-gray-100 text-right">
                        {hasCnbInQuote() ? <Input type="number" value={entryTicketCost.cnb} onChange={e => setEntryTicketCost({...entryTicketCost, cnb: parseFloat(e.target.value) || 0})} className="w-24 h-8 text-right bg-amber-50 border-amber-200 ml-auto" /> : <span className="text-gray-300">-</span>}
                      </td>
                      <td className="py-3 px-4 border-b border-gray-100 text-right font-mono font-semibold">
                        ₹{((entryTicketCost.adult * getAdultCount()) + (hasCwbInQuote() ? entryTicketCost.cwb * getCwbCount() : 0) + (hasCnbInQuote() ? entryTicketCost.cnb * getCnbCount() : 0)).toLocaleString()}
                      </td>
                    </tr>

                    {/* Ferry/Cruise Cost */}
                    <tr className="hover:bg-gray-50/50">
                      <td className="py-3 px-4 border-b border-gray-100 font-medium text-gray-700">Ferry / Cruise Cost</td>
                      <td className="py-3 px-4 border-b border-gray-100 text-right">
                        <Input type="number" value={ferryCruiseCost.adult} onChange={e => setFerryCruiseCost({...ferryCruiseCost, adult: parseFloat(e.target.value) || 0})} className="w-24 h-8 text-right bg-gray-50 border-gray-200 ml-auto" />
                      </td>
                      <td className="py-3 px-4 border-b border-gray-100 text-right">
                        {hasCwbInQuote() ? <Input type="number" value={ferryCruiseCost.cwb} onChange={e => setFerryCruiseCost({...ferryCruiseCost, cwb: parseFloat(e.target.value) || 0})} className="w-24 h-8 text-right bg-blue-50 border-blue-200 ml-auto" /> : <span className="text-gray-300">-</span>}
                      </td>
                      <td className="py-3 px-4 border-b border-gray-100 text-right">
                        {hasCnbInQuote() ? <Input type="number" value={ferryCruiseCost.cnb} onChange={e => setFerryCruiseCost({...ferryCruiseCost, cnb: parseFloat(e.target.value) || 0})} className="w-24 h-8 text-right bg-amber-50 border-amber-200 ml-auto" /> : <span className="text-gray-300">-</span>}
                      </td>
                      <td className="py-3 px-4 border-b border-gray-100 text-right font-mono font-semibold">
                        ₹{((ferryCruiseCost.adult * getAdultCount()) + (hasCwbInQuote() ? ferryCruiseCost.cwb * getCwbCount() : 0) + (hasCnbInQuote() ? ferryCruiseCost.cnb * getCnbCount() : 0)).toLocaleString()}
                      </td>
                    </tr>

                    {/* Vehicle Cost */}
                    <tr className="hover:bg-gray-50/50">
                      <td className="py-3 px-4 border-b border-gray-100 font-medium text-gray-700">Vehicle Cost</td>
                      <td className="py-3 px-4 border-b border-gray-100 text-right font-mono">₹{getTaxiPerPerson().adult.toLocaleString()}</td>
                      <td className={`py-3 px-4 border-b border-gray-100 text-right font-mono ${hasCwbInQuote() ? 'text-blue-600' : 'text-gray-300'}`}>{hasCwbInQuote() ? `₹${getTaxiPerPerson().cwb.toLocaleString()}` : '-'}</td>
                      <td className={`py-3 px-4 border-b border-gray-100 text-right font-mono ${hasCnbInQuote() ? 'text-amber-600' : 'text-gray-300'}`}>{hasCnbInQuote() ? `₹${getTaxiPerPerson().cnb.toLocaleString()}` : '-'}</td>
                      <td className="py-3 px-4 border-b border-gray-100 text-right font-mono font-semibold">₹{(taxiPricingMode === "manual" ? parseFloat(manualVehiclePrice || 0) : parseFloat(cabCost || 0)).toLocaleString()}</td>
                    </tr>

                    {/* Misc Cost */}
                    <tr className="hover:bg-gray-50/50">
                      <td className="py-3 px-4 border-b border-gray-100 font-medium text-gray-700">Misc Cost</td>
                      <td className="py-3 px-4 border-b border-gray-100 text-right">
                        <Input type="number" value={miscCost.adult} onChange={e => setMiscCost({...miscCost, adult: parseFloat(e.target.value) || 0})} className="w-24 h-8 text-right bg-gray-50 border-gray-200 ml-auto" />
                      </td>
                      <td className="py-3 px-4 border-b border-gray-100 text-right">
                        {hasCwbInQuote() ? <Input type="number" value={miscCost.cwb} onChange={e => setMiscCost({...miscCost, cwb: parseFloat(e.target.value) || 0})} className="w-24 h-8 text-right bg-blue-50 border-blue-200 ml-auto" /> : <span className="text-gray-300">-</span>}
                      </td>
                      <td className="py-3 px-4 border-b border-gray-100 text-right">
                        {hasCnbInQuote() ? <Input type="number" value={miscCost.cnb} onChange={e => setMiscCost({...miscCost, cnb: parseFloat(e.target.value) || 0})} className="w-24 h-8 text-right bg-amber-50 border-amber-200 ml-auto" /> : <span className="text-gray-300">-</span>}
                      </td>
                      <td className="py-3 px-4 border-b border-gray-100 text-right font-mono font-semibold">
                        ₹{((miscCost.adult * getAdultCount()) + (hasCwbInQuote() ? miscCost.cwb * getCwbCount() : 0) + (hasCnbInQuote() ? miscCost.cnb * getCnbCount() : 0)).toLocaleString()}
                      </td>
                    </tr>

                    {/* Mark Up Row */}
                    <tr className="bg-emerald-50/50 hover:bg-emerald-50">
                      <td className="py-3 px-4 border-b border-gray-200 font-semibold text-emerald-700">
                        Mark Up {markupMode === "percentage" && `(${markupPercentage}%)`}
                      </td>
                      {markupMode === "manual" ? (
                        <>
                          <td className="py-3 px-4 border-b border-gray-200 text-right">
                            <Input type="number" value={markupPerPerson.adult} onChange={e => setMarkupPerPerson({...markupPerPerson, adult: parseFloat(e.target.value) || 0})} className="w-24 h-8 text-right bg-white border-emerald-200 ml-auto" />
                          </td>
                          <td className="py-3 px-4 border-b border-gray-200 text-right">
                            {hasCwbInQuote() ? <Input type="number" value={markupPerPerson.cwb} onChange={e => setMarkupPerPerson({...markupPerPerson, cwb: parseFloat(e.target.value) || 0})} className="w-24 h-8 text-right bg-white border-emerald-200 ml-auto" /> : <span className="text-gray-300">-</span>}
                          </td>
                          <td className="py-3 px-4 border-b border-gray-200 text-right">
                            {hasCnbInQuote() ? <Input type="number" value={markupPerPerson.cnb} onChange={e => setMarkupPerPerson({...markupPerPerson, cnb: parseFloat(e.target.value) || 0})} className="w-24 h-8 text-right bg-white border-emerald-200 ml-auto" /> : <span className="text-gray-300">-</span>}
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="py-3 px-4 border-b border-gray-200 text-right font-mono text-emerald-700">₹{getTotalPerPerson().markupAdult.toLocaleString()}</td>
                          <td className={`py-3 px-4 border-b border-gray-200 text-right font-mono ${hasCwbInQuote() ? 'text-emerald-700' : 'text-gray-300'}`}>{hasCwbInQuote() ? `₹${getTotalPerPerson().markupCwb.toLocaleString()}` : '-'}</td>
                          <td className={`py-3 px-4 border-b border-gray-200 text-right font-mono ${hasCnbInQuote() ? 'text-emerald-700' : 'text-gray-300'}`}>{hasCnbInQuote() ? `₹${getTotalPerPerson().markupCnb.toLocaleString()}` : '-'}</td>
                        </>
                      )}
                      <td className="py-3 px-4 border-b border-gray-200 text-right">
                        {markupMode === "percentage" ? (
                          <div className="flex items-center justify-end gap-2">
                            <Input 
                              type="number" 
                              value={markupPercentage} 
                              onChange={e => setMarkupPercentage(parseFloat(e.target.value) || 0)} 
                              className="w-20 h-8 text-right bg-white border-emerald-300" 
                              placeholder="%"
                            />
                            <span className="text-emerald-700 font-medium">%</span>
                          </div>
                        ) : (
                          <span className="font-mono font-semibold text-emerald-700">
                            ₹{((markupPerPerson.adult * getAdultCount()) + (hasCwbInQuote() ? markupPerPerson.cwb * getCwbCount() : 0) + (hasCnbInQuote() ? markupPerPerson.cnb * getCnbCount() : 0)).toLocaleString()}
                          </span>
                        )}
                      </td>
                    </tr>

                    {/* TOTAL COST PP Row */}
                    <tr className="bg-gray-800 text-white">
                      <td className="py-4 px-4 font-bold text-lg">TOTAL COST PP</td>
                      <td className="py-4 px-4 text-right font-mono font-bold text-lg">₹{getTotalPerPerson().adult.toLocaleString()}</td>
                      <td className={`py-4 px-4 text-right font-mono font-bold text-lg ${hasCwbInQuote() ? 'text-blue-300' : 'text-gray-500'}`}>{hasCwbInQuote() ? `₹${getTotalPerPerson().cwb.toLocaleString()}` : '-'}</td>
                      <td className={`py-4 px-4 text-right font-mono font-bold text-lg ${hasCnbInQuote() ? 'text-amber-300' : 'text-gray-500'}`}>{hasCnbInQuote() ? `₹${getTotalPerPerson().cnb.toLocaleString()}` : '-'}</td>
                      <td className="py-4 px-4 text-right font-mono font-bold text-xl text-emerald-400">₹{getGrandTotal().toLocaleString()}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Summary Footer */}
              <div className="mt-6 p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg border border-emerald-200">
                <div className={`grid ${hasCwbInQuote() || hasCnbInQuote() ? 'grid-cols-4' : 'grid-cols-2'} gap-4 text-center`}>
                  <div>
                    <p className="text-sm text-gray-500">Adults × {getAdultCount()}</p>
                    <p className="text-lg font-bold text-gray-800">₹{getTotalPerPerson().adult.toLocaleString()} per adult</p>
                  </div>
                  {hasCwbInQuote() && (
                  <div>
                    <p className="text-sm text-blue-600">CWB × {getCwbCount()}</p>
                    <p className="text-lg font-bold text-blue-700">₹{(getTotalPerPerson().cwb * getCwbCount()).toLocaleString()}</p>
                  </div>
                  )}
                  {hasCnbInQuote() && (
                  <div>
                    <p className="text-sm text-amber-600">CNB × {getCnbCount()}</p>
                    <p className="text-lg font-bold text-amber-700">₹{(getTotalPerPerson().cnb * getCnbCount()).toLocaleString()}</p>
                  </div>
                  )}
                  <div className="border-l-2 border-emerald-300 pl-4">
                    <p className="text-sm text-emerald-600">Grand Total</p>
                    <p className="text-2xl font-bold text-emerald-700">₹{getGrandTotal().toLocaleString()}</p>
                  </div>
                </div>
              </div>

              <Button onClick={saveQuote} className="w-full bg-green-600 hover:bg-green-700 h-12 text-lg mt-6">
                <Save className="w-5 h-5 mr-2" /> Save & Send Quote
              </Button>
            </CardContent>
          </Card>
          </TabsContent>
        </Tabs>

        {/* Activity Selection Modal */}
        <Dialog open={showActivityModal} onOpenChange={setShowActivityModal}>
            <DialogContent className="bg-white border-gray-200 text-gray-800 max-w-3xl h-[80vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle>Add Activity for Day {currentDayForActivity}</DialogTitle>
                </DialogHeader>
                <div className="p-4">
                    <Input 
                        placeholder={`Search ${quote?.destination || ''} activities...`} 
                        className="bg-gray-50 border-gray-200" 
                        value={activitySearch}
                        onChange={(e) => setActivitySearch(e.target.value)}
                    />
                    {relevantCities.length > 0 && (
                        <p className="text-xs text-green-400 mt-2">
                            Filtering for: {relevantCities.map(c => c.charAt(0).toUpperCase() + c.slice(1)).join(", ")}
                        </p>
                    )}
                </div>
                <div className="flex-1 overflow-y-auto pr-2 px-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {filteredSightseeing.length === 0 ? (
                            <p className="text-gray-500 col-span-2 text-center py-8">
                                No activities found for this destination. Try searching or add new resources.
                            </p>
                        ) : (
                            filteredSightseeing.map(tour => (
                                <div key={tour.id} className="p-4 border border-gray-200 rounded bg-gray-50/50 flex justify-between items-start hover:border-primary-500 transition-colors">
                                    <div>
                                        <p className="text-gray-800 font-medium text-lg">{tour.tour_name}</p>
                                        <p className="text-sm text-gray-500">{tour.destination}</p>
                                        <p className="text-xs text-gray-400 mt-1 line-clamp-2">{tour.description}</p>
                                        <p className="text-green-400 font-bold mt-2">₹{tour.price_adult}</p>
                                    </div>
                                    <Button size="sm" className="bg-primary-700 hover:bg-primary-800 shrink-0 ml-4" onClick={() => addActivity(tour)}>
                                        <Plus className="w-4 h-4" /> Add
                                    </Button>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>

        {/* Entry Ticket Modal */}
        <Dialog open={showEntryTicketModal} onOpenChange={setShowEntryTicketModal}>
            <DialogContent className="bg-white border-gray-200 text-gray-800 max-w-3xl h-[80vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle>Add Entry Ticket for Day {currentDayForActivity}</DialogTitle>
                </DialogHeader>
                <div className="p-4">
                    <Input 
                        placeholder="Search entry tickets..." 
                        className="bg-gray-50 border-gray-200" 
                        value={entryTicketSearch}
                        onChange={(e) => setEntryTicketSearch(e.target.value)}
                    />
                </div>
                <div className="flex-1 overflow-y-auto pr-2 px-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {entryTickets.filter(t => {
                            const q = entryTicketSearch.toLowerCase();
                            const name = (t.attraction_name || t.name || '').toLowerCase();
                            return (!q || name.includes(q) || (t.destination || '').toLowerCase().includes(q));
                        }).length === 0 ? (
                            <p className="text-gray-500 col-span-2 text-center py-8">
                                No entry tickets found. Add them in Resource Manager.
                            </p>
                        ) : (
                            entryTickets.filter(t => {
                                const q = entryTicketSearch.toLowerCase();
                                const name = (t.attraction_name || t.name || '').toLowerCase();
                                return (!q || name.includes(q) || (t.destination || '').toLowerCase().includes(q));
                            }).map(ticket => (
                                <div key={ticket.id} className="p-4 border border-gray-200 rounded bg-amber-50/30 flex justify-between items-start hover:border-amber-400 transition-colors">
                                    <div>
                                        <p className="text-gray-800 font-medium text-lg">{ticket.attraction_name || ticket.name}</p>
                                        <p className="text-sm text-gray-500">{ticket.destination}</p>
                                        {ticket.description && <p className="text-xs text-gray-400 mt-1 line-clamp-2">{ticket.description}</p>}
                                        <div className="flex gap-3 mt-2">
                                            <span className="text-amber-600 font-bold">Adult: {ticket.currency || '₹'} {ticket.price_adult || 0}</span>
                                            {ticket.price_cwb > 0 && <span className="text-blue-500 text-sm">CWB: {ticket.currency || '₹'} {ticket.price_cwb}</span>}
                                            {ticket.price_cnb > 0 && <span className="text-amber-500 text-sm">CNB: {ticket.currency || '₹'} {ticket.price_cnb}</span>}
                                        </div>
                                    </div>
                                    <Button size="sm" className="bg-amber-600 hover:bg-amber-700 shrink-0 ml-4" onClick={() => addEntryTicket(ticket)}>
                                        <Plus className="w-4 h-4" /> Add
                                    </Button>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>

        {/* Manual Activity Modal — used for BOTH add and edit */}
        <Dialog
          open={showManualActivityModal}
          onOpenChange={(open) => {
            setShowManualActivityModal(open);
            if (!open) {
              setEditingActivityIdx(null);
              setManualActivity({ title: "", description: "", price: 0, tour_type: "SIC" });
            }
          }}
        >
            <DialogContent className="bg-white border-gray-200 text-gray-800 max-w-md">
                <DialogHeader>
                    <DialogTitle>
                      {editingActivityIdx !== null ? `Edit Activity (Day ${currentDayForActivity})` : `Add Manual Activity for Day ${currentDayForActivity}`}
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 p-4">
                    <div>
                        <Label>Activity Name *</Label>
                        <Input 
                            value={manualActivity.title} 
                            onChange={e => setManualActivity({...manualActivity, title: e.target.value})} 
                            className="bg-gray-50 border-gray-200 mt-1" 
                            placeholder="e.g. City Walking Tour"
                            data-testid="manual-activity-name"
                        />
                    </div>
                    <div>
                        <Label>Description</Label>
                        <textarea 
                            value={manualActivity.description} 
                            onChange={e => setManualActivity({...manualActivity, description: e.target.value})} 
                            className="w-full mt-1 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm min-h-[80px]" 
                            placeholder="Brief description..."
                        />
                    </div>
                    <div>
                        <Label>Tour Type</Label>
                        <Select
                            value={manualActivity.tour_type || "SIC"}
                            onValueChange={(val) => setManualActivity({...manualActivity, tour_type: val})}
                        >
                            <SelectTrigger className="bg-gray-50 border-gray-200 mt-1" data-testid="manual-activity-tour-type">
                                <SelectValue placeholder="Select tour type" />
                            </SelectTrigger>
                            <SelectContent className="bg-white">
                                <SelectItem value="SIC">Seat in Coach (SIC)</SelectItem>
                                <SelectItem value="PVT">Private (PVT)</SelectItem>
                                <SelectItem value="NONE">No Transfer</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>Price (per adult)</Label>
                        <Input 
                            type="number"
                            value={manualActivity.price} 
                            onChange={e => setManualActivity({...manualActivity, price: e.target.value})} 
                            className="bg-gray-50 border-gray-200 mt-1" 
                            placeholder="0"
                            data-testid="manual-activity-price"
                        />
                    </div>
                    <Button 
                        onClick={addManualActivity} 
                        className="w-full bg-purple-600 hover:bg-purple-700"
                        data-testid="add-manual-activity-btn"
                    >
                        {editingActivityIdx !== null ? (
                          <><Edit className="w-4 h-4 mr-2" /> Update Activity</>
                        ) : (
                          <><Plus className="w-4 h-4 mr-2" /> Add Activity</>
                        )}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>

        <Dialog open={showTransferModal} onOpenChange={setShowTransferModal}>
            <DialogContent className="bg-white border-gray-200 text-gray-800 max-w-3xl h-[70vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle>Add Transport</DialogTitle>
                </DialogHeader>
                <div className="flex-1 overflow-y-auto pr-2 p-4">
                    {transfers.length === 0 ? (
                        <p className="text-gray-500 text-center py-8">No transfer rates found. Please add in Resource Manager.</p>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {transfers.filter(t => !quote?.destination || t.destination.includes(quote.destination)).map((t, idx) => (
                                <div key={idx} className="p-4 border border-gray-200 rounded bg-gray-50/50 hover:border-primary-500 transition-colors">
                                    <div className="flex justify-between items-start mb-2">
                                        <div className="bg-blue-900/30 text-blue-400 text-xs px-2 py-1 rounded font-medium flex items-center">
                                            <Truck className="w-3 h-3 mr-1" /> {t.vehicle_type}
                                        </div>
                                    </div>
                                    <h3 className="text-gray-800 font-semibold text-lg">{t.destination}</h3>
                                    <div className="mt-2">
                                        <p className="text-2xl font-bold text-green-400">₹{t.price}</p>
                                        <p className="text-xs text-gray-400">{t.rate_type === 'per_day' ? 'Per Day' : 'Fixed Cost'}</p>
                                    </div>
                                    <Button onClick={() => addTransfer(t)} className="w-full mt-4 bg-primary-700 hover:bg-primary-800">
                                        Select
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>

        {/* Edit Enquiry modal — opens automatically for duplicated quotes */}
        <Dialog open={showEnquiryEdit} onOpenChange={setShowEnquiryEdit}>
          <DialogContent className="bg-white max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="enquiry-edit-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-emerald-700">
                <Edit className="w-5 h-5" /> Edit Enquiry Details
              </DialogTitle>
            </DialogHeader>
            <p className="text-xs text-gray-500 -mt-2">Update the trip basics — destination, dates, pax and preferences. Hotels, activities and pricing stay intact.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
              {/* 1. Guest Name */}
              <div className="sm:col-span-2">
                <Label className="text-sm">Guest Name</Label>
                <Input value={enquiryForm.client_name} onChange={e => setEnquiryForm({ ...enquiryForm, client_name: e.target.value })} data-testid="enq-client-name" placeholder="Client / Guest Name" />
              </div>
              {/* 2. Destination */}
              <div className="sm:col-span-2">
                <Label className="text-sm">Destination *</Label>
                <Input value={enquiryForm.destination} onChange={e => setEnquiryForm({ ...enquiryForm, destination: e.target.value })} data-testid="enq-destination" placeholder="e.g. Kerala, Goa" />
              </div>
              {/* 3. Covered Cities */}
              <div className="sm:col-span-2">
                <Label className="text-sm">Covered Cities</Label>
                <Input value={enquiryForm.covered_cities} onChange={e => setEnquiryForm({ ...enquiryForm, covered_cities: e.target.value })} data-testid="enq-covered-cities" placeholder="e.g. Munnar, Alleppey, Kovalam" />
              </div>
              {/* 4. Pickup & Drop */}
              <div>
                <Label className="text-sm">Pick Up Location</Label>
                <Input value={enquiryForm.pickup_location} onChange={e => setEnquiryForm({ ...enquiryForm, pickup_location: e.target.value })} data-testid="enq-pickup" placeholder="e.g. Cochin Airport" />
              </div>
              <div>
                <Label className="text-sm">Drop Location</Label>
                <Input value={enquiryForm.drop_location} onChange={e => setEnquiryForm({ ...enquiryForm, drop_location: e.target.value })} data-testid="enq-drop" placeholder="e.g. Trivandrum Airport" />
              </div>
              {/* 5. Travel & Return Date */}
              <div>
                <Label className="text-sm">Travel Date *</Label>
                <Input type="date" value={enquiryForm.travel_date} onChange={e => setEnquiryForm({ ...enquiryForm, travel_date: e.target.value })} data-testid="enq-travel-date" />
              </div>
              <div>
                <Label className="text-sm">Return Date *</Label>
                <Input type="date" value={enquiryForm.return_date} onChange={e => setEnquiryForm({ ...enquiryForm, return_date: e.target.value })} data-testid="enq-return-date" />
              </div>
              {/* 6. No of Nights (auto) */}
              <div className="sm:col-span-2">
                <Label className="text-sm text-gray-400">No of Nights (auto-calculated)</Label>
                <Input
                  readOnly
                  value={
                    enquiryForm.travel_date && enquiryForm.return_date
                      ? Math.max(0, Math.ceil((new Date(enquiryForm.return_date) - new Date(enquiryForm.travel_date)) / 86400000))
                      : ""
                  }
                  className="bg-gray-100 text-gray-500 cursor-not-allowed"
                  placeholder="Auto"
                />
              </div>
              {/* 7. Adults */}
              <div>
                <Label className="text-sm">No of Adults</Label>
                <Input type="number" min="1" value={enquiryForm.pax_adults} onChange={e => setEnquiryForm({ ...enquiryForm, pax_adults: parseInt(e.target.value || 1, 10) })} data-testid="enq-adults" />
              </div>
                  {/* 8. CWB with Age dropdown */}
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <Label className="text-sm">CWB (Child With Bed)</Label>
                  <Input type="number" min="0" value={enquiryForm.cwb} onChange={e => setEnquiryForm({ ...enquiryForm, cwb: parseInt(e.target.value || 0, 10) })} data-testid="enq-cwb" placeholder="0" />
                </div>
                <div className="flex-1">
                  <Label className="text-sm">CWB Age</Label>
                  <select value={enquiryForm.cwb_age} onChange={e => setEnquiryForm({ ...enquiryForm, cwb_age: e.target.value })} className="w-full h-10 rounded-md border border-gray-200 bg-gray-50 px-3 text-sm" data-testid="enq-cwb-age">
                    <option value="">Select Age</option>
                    {[6,7,8,9,10,11].map(yr => <option key={yr} value={`${yr} Yrs`}>{yr} Yrs</option>)}
                  </select>
                </div>
              </div>
              {/* 9. CNB with Age dropdown */}
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <Label className="text-sm">CNB (Child No Bed)</Label>
                  <Input type="number" min="0" value={enquiryForm.cnb} onChange={e => setEnquiryForm({ ...enquiryForm, cnb: parseInt(e.target.value || 0, 10) })} data-testid="enq-cnb" placeholder="0" />
                </div>
                <div className="flex-1">
                  <Label className="text-sm">CNB Age</Label>
                  <select value={enquiryForm.cnb_age} onChange={e => setEnquiryForm({ ...enquiryForm, cnb_age: e.target.value })} className="w-full h-10 rounded-md border border-gray-200 bg-gray-50 px-3 text-sm" data-testid="enq-cnb-age">
                    <option value="">Select Age</option>
                    {[3,4,5,6,7,8,9].map(yr => <option key={yr} value={`${yr} Yrs`}>{yr} Yrs</option>)}
                  </select>
                </div>
              </div>
              {/* 10. Infant with Age dropdown */}
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <Label className="text-sm">Infant</Label>
                  <Input type="number" min="0" value={enquiryForm.pax_infants} onChange={e => setEnquiryForm({ ...enquiryForm, pax_infants: parseInt(e.target.value || 0, 10) })} data-testid="enq-infants" placeholder="0" />
                </div>
                <div className="flex-1">
                  <Label className="text-sm">Infant Age</Label>
                  <select value={enquiryForm.infant_age} onChange={e => setEnquiryForm({ ...enquiryForm, infant_age: e.target.value })} className="w-full h-10 rounded-md border border-gray-200 bg-gray-50 px-3 text-sm" data-testid="enq-infant-age">
                    <option value="">Select Age</option>
                    {[0,1,2].map(yr => <option key={yr} value={`${yr} Yrs`}>{yr} Yrs</option>)}
                  </select>
                </div>
              </div>
              {/* 11. No of Rooms */}
              <div>
                <Label className="text-sm">No of Rooms</Label>
                <Input type="number" min="1" value={enquiryForm.no_of_rooms} onChange={e => setEnquiryForm({ ...enquiryForm, no_of_rooms: parseInt(e.target.value || 1, 10) })} data-testid="enq-rooms" />
              </div>
              {/* 12. Hotel Category */}
              <div>
                <Label className="text-sm">Hotel Category</Label>
                <select value={enquiryForm.hotel_category} onChange={e => setEnquiryForm({ ...enquiryForm, hotel_category: e.target.value })} className="w-full h-10 rounded-md border border-gray-200 bg-gray-50 px-3 text-sm" data-testid="enq-hotel-category">
                  <option value="budget">Budget</option>
                  <option value="3-star">3 Star</option>
                  <option value="3-star-deluxe">3 Star Deluxe</option>
                  <option value="4-star">4 Star</option>
                  <option value="4-star-deluxe">4 Star Deluxe</option>
                  <option value="5-star">5 Star</option>
                  <option value="5-star-luxury">5 Star Luxury</option>
                  <option value="luxury">Luxury Resort</option>
                </select>
              </div>
              {/* 13. Meal Plan */}
              <div>
                <Label className="text-sm">Meal Plan</Label>
                <select value={enquiryForm.meal_plan} onChange={e => setEnquiryForm({ ...enquiryForm, meal_plan: e.target.value })} className="w-full h-10 rounded-md border border-gray-200 bg-gray-50 px-3 text-sm" data-testid="enq-meal-plan">
                  <option value="EP">EP (Room Only)</option>
                  <option value="CP">CP (Breakfast)</option>
                  <option value="MAP">MAP (Breakfast + Dinner)</option>
                  <option value="AP">AP (All Meals)</option>
                  <option value="AI">All Inclusive</option>
                </select>
              </div>
              {/* 14. With Hotel */}
              <div>
                <Label className="text-sm">With Hotel?</Label>
                <select value={enquiryForm.with_hotel} onChange={e => setEnquiryForm({ ...enquiryForm, with_hotel: e.target.value })} className="w-full h-10 rounded-md border border-gray-200 bg-gray-50 px-3 text-sm" data-testid="enq-with-hotel">
                  <option value="yes">Yes — include hotels</option>
                  <option value="no">No — land only / sightseeing only</option>
                </select>
              </div>
              {/* 15. Requirements */}
              <div className="sm:col-span-2">
                <Label className="text-sm">Requirements / Notes</Label>
                <textarea value={enquiryForm.requirements} onChange={e => setEnquiryForm({ ...enquiryForm, requirements: e.target.value })} rows={3} className="w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm" data-testid="enq-requirements" />
              </div>
            </div>
            <div className="flex gap-2 pt-3">
              <Button variant="outline" onClick={() => setShowEnquiryEdit(false)} className="flex-1">Cancel</Button>
              <Button
                onClick={async () => {
                  if (!enquiryForm.destination?.trim() || !enquiryForm.travel_date || !enquiryForm.return_date) {
                    toast.error("Destination, travel date and return date are required");
                    return;
                  }
                  if (new Date(enquiryForm.return_date) <= new Date(enquiryForm.travel_date)) {
                    toast.error("Return date must be after travel date");
                    return;
                  }
                  try {
                    setEnquirySaving(true);
                    await axios.put(`${API}/quotes/${id}`, {
                      destination: enquiryForm.destination.trim(),
                      covered_cities: enquiryForm.covered_cities,
                      pickup_location: enquiryForm.pickup_location,
                      drop_location: enquiryForm.drop_location,
                      travel_date: enquiryForm.travel_date,
                      return_date: enquiryForm.return_date,
                      pax_adults: enquiryForm.pax_adults,
                      cwb: enquiryForm.cwb,
                      cwb_age: enquiryForm.cwb_age,
                      cnb: enquiryForm.cnb,
                      cnb_age: enquiryForm.cnb_age,
                      pax_infants: enquiryForm.pax_infants,
                      infant_age: enquiryForm.infant_age,
                      no_of_rooms: enquiryForm.no_of_rooms,
                      hotel_category: enquiryForm.hotel_category,
                      meal_plan: enquiryForm.meal_plan,
                      with_hotel: enquiryForm.with_hotel,
                      client_name: enquiryForm.client_name,
                      client_phone: enquiryForm.client_phone,
                      client_email: enquiryForm.client_email,
                      requirements: enquiryForm.requirements,
                      is_duplicate_pending_edit: false,
                    }, { headers: { Authorization: `Bearer ${token}` } });
                    toast.success("Enquiry updated — reload to refresh dependent fields");
                    setShowEnquiryEdit(false);
                    // Refresh the page state by re-fetching
                    window.location.reload();
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Failed to update enquiry");
                  } finally {
                    setEnquirySaving(false);
                  }
                }}
                disabled={enquirySaving}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                data-testid="enq-save-btn"
              >
                {enquirySaving ? "Saving..." : "Save & Reload"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default QuoteBuilder;
